import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Layout from '../components/Layout';
import ComplianceDashboardNudge from '../components/compliance/ComplianceDashboardNudge';
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Bell,
  Building2,
  BriefcaseBusiness,
  Calendar,
  CheckCircle2,
  Circle,
  Clock,
  DollarSign,
  Download,
  Eye,
  EyeOff,
  FileText,
  MessageCircle,
  Package,
  Receipt,
  RefreshCw,
  Share2,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Lightbulb,
  Target,
  Info,
  ListChecks,
  Smile,
  TrendingUp,
  UserRoundPlus,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const isBirthdayReminder = (reminder) => {
  const searchable = [
    reminder?.title,
    reminder?.type,
    reminder?.reminder_type,
    reminder?.category,
    reminder?.description,
  ]
    .filter(Boolean)
    .join(' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  return searchable.includes('aniversario') || searchable.includes('birthday');
};

const DEFAULT_DASHBOARD_PREFERENCES = {
  preset: 'complete',
  mode: 'intelligent',
  sections: {
    today: true,
    management: true,
    intelligence: true,
  },
  blocks: {
    heroMetrics: true,
    quickLinks: true,
    recommendedActions: true,
    predictiveCards: true,
    performance: true,
    dailyReading: true,
    executiveAssistant: true,
    dailyGoals: true,
    managementConsultant: true,
  },
};

const ESSENTIAL_DASHBOARD_PREFERENCES = {
  preset: 'essential',
  mode: 'operational',
  sections: {
    today: true,
    management: false,
    intelligence: false,
  },
  blocks: {
    heroMetrics: false,
    quickLinks: true,
    recommendedActions: false,
    predictiveCards: false,
    performance: false,
    dailyReading: false,
    executiveAssistant: false,
    dailyGoals: false,
    managementConsultant: false,
  },
};

const money = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const number = (value) => Number(value || 0).toLocaleString('pt-BR');

const safeDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const localDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const monthKey = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const monthOptions = (amount = 24) =>
  Array.from({ length: amount }, (_, index) => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() - index);
    return {
      value: monthKey(date),
      label: date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
    };
  });

const roundedRect = (context, x, y, width, height, radius) => {
  const safeRadius = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.roundRect(x, y, width, height, safeRadius);
};

const appointmentDateKey = (appointment) => {
  const raw =
    appointment?.appointment_date ||
    appointment?.date ||
    appointment?.start_date ||
    appointment?.start;

  if (!raw) return '';
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }

  const parsed = safeDate(raw);
  return parsed ? localDateKey(parsed) : '';
};

const appointmentTime = (appointment) => {
  const raw =
    appointment?.appointment_time ||
    appointment?.time ||
    appointment?.start_time ||
    appointment?.start;

  if (!raw) return '--:--';
  if (typeof raw === 'string') {
    const timeMatch = raw.match(/(?:T|\s)(\d{2}:\d{2})/);
    if (timeMatch) return timeMatch[1];
    if (/^\d{2}:\d{2}/.test(raw)) return raw.slice(0, 5);
  }

  const parsed = safeDate(raw);
  return parsed
    ? parsed.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : '--:--';
};

const normalizedStatus = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const statusLabel = (appointment) => {
  const status = normalizedStatus(appointment?.status);
  if (status.includes('confirm')) return 'Confirmado';
  if (status.includes('cancel')) return 'Cancelado';
  if (status.includes('conclu') || status.includes('realiz')) return 'Concluído';
  if (status.includes('falt') || status.includes('no_show')) return 'Faltou';
  return appointment?.status || 'Pendente';
};

const statusClass = (appointment) => {
  const status = normalizedStatus(appointment?.status);
  if (status.includes('confirm')) return 'bg-emerald-50 text-emerald-700';
  if (status.includes('cancel') || status.includes('falt')) return 'bg-red-50 text-red-700';
  if (status.includes('conclu') || status.includes('realiz')) return 'bg-blue-50 text-blue-700';
  return 'bg-amber-50 text-amber-700';
};

const Delta = ({ value, suffix = '%' }) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return <span className="text-xs text-gray-400">Sem comparativo</span>;
  }

  const numeric = Number(value);
  const positive = numeric >= 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;

  return (
    <span
      className={`inline-flex items-center text-xs font-semibold ${
        positive ? 'text-emerald-600' : 'text-red-600'
      }`}
    >
      <Icon className="w-3.5 h-3.5 mr-1" />
      {Math.abs(numeric).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
      {suffix}
    </span>
  );
};

const ExecutiveCard = ({ title, value, description, icon: Icon, onClick, delta }) => {
  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`w-full bg-white rounded-xl border border-gray-200 p-5 text-left shadow-sm ${
        onClick ? 'hover:border-purple-300 hover:shadow-md transition-all' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2 truncate">{value}</p>
          <div className="mt-2 flex items-center gap-2 min-h-[20px]">
            {delta !== undefined ? <Delta value={delta} /> : null}
            {description ? <span className="text-xs text-gray-500">{description}</span> : null}
          </div>
        </div>
        <div className="w-11 h-11 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-purple-600" />
        </div>
      </div>
    </Tag>
  );
};

const HealthCard = ({ title, status, description, icon: Icon, onClick }) => {
  const styles = {
    good: {
      label: 'Saudável',
      badge: 'bg-emerald-50 text-emerald-700',
      icon: 'bg-emerald-50 text-emerald-600',
    },
    attention: {
      label: 'Atenção',
      badge: 'bg-amber-50 text-amber-700',
      icon: 'bg-amber-50 text-amber-600',
    },
    critical: {
      label: 'Crítico',
      badge: 'bg-red-50 text-red-700',
      icon: 'bg-red-50 text-red-600',
    },
  }[status || 'good'];

  return (
    <button
      type="button"
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-5 text-left shadow-sm hover:border-purple-300 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${styles.icon}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles.badge}`}>
          {styles.label}
        </span>
      </div>
      <h3 className="font-semibold text-gray-900 mt-4">{title}</h3>
      <p className="text-sm text-gray-500 mt-1">{description}</p>
    </button>
  );
};

const ProgressMetric = ({ label, value, detail, unavailable = false }) => {
  const numeric = unavailable
    ? 0
    : Math.max(0, Math.min(100, Number(value || 0)));

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <p className="text-sm font-medium text-gray-800">{label}</p>
          {detail ? <p className="text-xs text-gray-500 mt-0.5">{detail}</p> : null}
        </div>
        <span className={`text-sm font-bold ${unavailable ? 'text-gray-400' : 'text-gray-900'}`}>
          {unavailable
            ? 'Sem base'
            : `${numeric.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%`}
        </span>
      </div>
      <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            unavailable ? 'bg-gray-300' : 'bg-purple-600'
          }`}
          style={{ width: unavailable ? '100%' : `${numeric}%` }}
        />
      </div>
    </div>
  );
};

const DashboardSkeleton = () => (
  <div className="space-y-8 animate-pulse" aria-label="Carregando dashboard">
    <div className="h-64 rounded-2xl bg-gray-200" />
    <div className="space-y-4">
      <div className="h-7 w-48 rounded bg-gray-200" />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="h-96 rounded-xl bg-gray-200 xl:col-span-2" />
        <div className="h-96 rounded-xl bg-gray-200" />
      </div>
    </div>
    <div className="space-y-4">
      <div className="h-7 w-64 rounded bg-gray-200" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((item) => (
          <div key={item} className="h-36 rounded-xl bg-gray-200" />
        ))}
      </div>
      <div className="h-80 rounded-xl bg-gray-200" />
    </div>
  </div>
);

const Dashboard = () => {
  const {
    user,
    dashboardPreferences: storedDashboardPreferences,
    updateDashboardPreferences: persistDashboardPreferences,
  } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showFinancialValues, setShowFinancialValues] = useState(true);
  const [showDashboardSettings, setShowDashboardSettings] = useState(false);
  const [showClinicScoreDetails, setShowClinicScoreDetails] = useState(false);
  const [showMonthlySummary, setShowMonthlySummary] = useState(false);
  const [selectedSummaryMonth, setSelectedSummaryMonth] = useState(() => {
    const previousMonth = new Date();
    previousMonth.setDate(1);
    previousMonth.setMonth(previousMonth.getMonth() - 1);
    return monthKey(previousMonth);
  });
  const [clinicBranding, setClinicBranding] = useState({
    name: '',
    logoUrl: '',
  });
  const [exportingSummary, setExportingSummary] = useState(false);
  const [completedDailyGoals, setCompletedDailyGoals] = useState([]);
  const [consultantPeriod, setConsultantPeriod] = useState('90');
  const [dashboardPreferences, setDashboardPreferences] = useState(
    DEFAULT_DASHBOARD_PREFERENCES
  );
  const [data, setData] = useState({
    patients: [],
    treatments: [],
    appointments: [],
    reminders: [],
    inventory: {},
    billing: {},
    financial: {},
    financialHistory: {},
    managementPeriods: {},
    generatedAt: null,
    timezone: 'America/Sao_Paulo',
    partialErrors: [],
  });

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'http://localhost:8001'
      : '');

  const financialVisibilityUserKey = String(
    user?.id ||
      user?._id ||
      user?.user_id ||
      user?.email ||
      user?.username ||
      'anonymous'
  ).trim();

  const financialVisibilityStorageKey =
    `dashboard_show_financial_values:${financialVisibilityUserKey}`;

  const dashboardPreferencesStorageKey =
    `dashboard_preferences:${financialVisibilityUserKey}`;

  const dailyGoalsStorageKey =
    `dashboard_daily_goals:${financialVisibilityUserKey}:${localDateKey()}`;

  const normalizedRole = normalizedStatus(
    user?.role || user?.user_type || user?.profile || user?.access_level
  );

  const explicitPermissions = Array.isArray(user?.permissions)
    ? user.permissions.map((item) => normalizedStatus(item))
    : [];

  const explicitFinancialRestriction =
    user?.can_view_financial === false ||
    user?.permissions?.financial === false ||
    user?.permissions?.billing === false ||
    explicitPermissions.includes('deny_financial') ||
    explicitPermissions.includes('sem_financeiro');

  const canViewFinancial =
    !explicitFinancialRestriction ||
    normalizedRole.includes('admin') ||
    normalizedRole.includes('owner') ||
    normalizedRole.includes('propriet');

  const financialValuesVisible = canViewFinancial && showFinancialValues;

  const maskedMoney = (value) =>
    financialValuesVisible ? money(value) : 'R$ •••••';

  const toggleFinancialValues = () => {
    if (!canViewFinancial) return;

    setShowFinancialValues((current) => {
      const next = !current;
      try {
        localStorage.setItem(financialVisibilityStorageKey, String(next));
      } catch {
        // Mantém a preferência apenas na sessão.
      }
      return next;
    });
  };

  const saveDashboardPreferences = (
    nextPreferences,
    { preset = 'custom', manuallyCustomized = true } = {}
  ) => {
    const preferencesToSave = {
      ...nextPreferences,
      preset,
    };
    setDashboardPreferences(preferencesToSave);

    try {
      localStorage.setItem(
        dashboardPreferencesStorageKey,
        JSON.stringify(preferencesToSave)
      );
    } catch {
      // Mantém a configuração somente durante a sessão.
    }

    persistDashboardPreferences?.({
      preset,
      mode: preferencesToSave.mode,
      sections: preferencesToSave.sections,
      blocks: preferencesToSave.blocks,
      manually_customized: manuallyCustomized,
    }).catch((error) => {
      console.error('Erro ao salvar preferências do Dashboard:', error);
      showToast?.('A personalização ficou salva neste navegador, mas não pôde ser sincronizada.', 'error');
    });
  };

  const updateDashboardMode = (mode) => {
    saveDashboardPreferences({
      ...dashboardPreferences,
      mode,
    });
  };

  const toggleDashboardSection = (section) => {
    saveDashboardPreferences({
      ...dashboardPreferences,
      sections: {
        ...dashboardPreferences.sections,
        [section]: !dashboardPreferences.sections[section],
      },
    });
  };

  const toggleDashboardBlock = (block) => {
    saveDashboardPreferences({
      ...dashboardPreferences,
      blocks: {
        ...dashboardPreferences.blocks,
        [block]: !dashboardPreferences.blocks[block],
      },
    });
  };

  const restoreDashboardDefaults = () => {
    saveDashboardPreferences(DEFAULT_DASHBOARD_PREFERENCES, {
      preset: 'complete',
      manuallyCustomized: false,
    });
  };

  const applyDashboardPreset = (preset) => {
    const nextPreferences =
      preset === 'essential'
        ? ESSENTIAL_DASHBOARD_PREFERENCES
        : DEFAULT_DASHBOARD_PREFERENCES;
    saveDashboardPreferences(nextPreferences, {
      preset,
      manuallyCustomized: false,
    });
  };

  const navigateToFinancial = (tab = 'reports', extraParams = {}) => {
    const params = new URLSearchParams({ tab });

    Object.entries(extraParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined && String(value) !== '') {
        params.set(key, String(value));
      }
    });

    navigate(`/financeiro?${params.toString()}`);
  };

  const navigateToPatients = (filter = '') => {
    const params = new URLSearchParams({ modo: 'lista' });
    if (filter) params.set('filter', filter);
    navigate(`/prontuarios?${params.toString()}`);
  };

  const navigateToTreatments = (filter = '') => {
    const params = new URLSearchParams({
      modo: 'lista',
      tab: 'treatments',
    });
    if (filter) params.set('filter', filter);
    navigate(`/prontuarios?${params.toString()}`);
  };


  const loadDashboard = async ({ silent = false } = {}) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${backendUrl}/api/dashboard/executive`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        let detail = 'Não foi possível carregar o dashboard executivo.';
        try {
          const errorBody = await response.json();
          detail =
            typeof errorBody?.detail === 'string'
              ? errorBody.detail
              : detail;
        } catch {
          // Mantém a mensagem padrão quando a resposta não for JSON.
        }
        throw new Error(detail);
      }

      const dashboard = await response.json();

      setData({
        patients: Array.isArray(dashboard?.patients) ? dashboard.patients.filter(p => !p.is_inactive) : [],
        treatments: Array.isArray(dashboard?.treatments) ? dashboard.treatments : [],
        appointments: Array.isArray(dashboard?.appointments) ? dashboard.appointments : [],
        reminders: Array.isArray(dashboard?.reminders) ? dashboard.reminders : [],
        inventory: dashboard?.inventory || {},
        billing: dashboard?.billing || {},
        financial: dashboard?.financial || {},
        financialHistory: dashboard?.financialHistory || {},
        managementPeriods: dashboard?.managementPeriods || {},
        generatedAt: dashboard?.generated_at || null,
        timezone: dashboard?.timezone || 'America/Sao_Paulo',
        partialErrors: Array.isArray(dashboard?.partial_errors)
          ? dashboard.partial_errors
          : [],
      });

      if (Array.isArray(dashboard?.partial_errors) && dashboard.partial_errors.length > 0) {
        console.warn(
          'Dashboard carregado com fontes indisponíveis:',
          dashboard.partial_errors
        );
        showToast?.(
          'Alguns indicadores não puderam ser atualizados. Os demais dados foram carregados.',
          'warning'
        );
      }
    } catch (error) {
      console.error('Erro ao carregar dashboard executivo:', error);
      showToast?.(
        error?.message || 'Não foi possível carregar os indicadores do dashboard.',
        'error'
      );
    } finally {
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!user) {
      setDashboardPreferences(DEFAULT_DASHBOARD_PREFERENCES);
      return;
    }

    try {
      const serverHasPreferences =
        storedDashboardPreferences?.manually_customized === true ||
        Object.keys(storedDashboardPreferences?.sections || {}).length > 0 ||
        Object.keys(storedDashboardPreferences?.blocks || {}).length > 0;

      if (serverHasPreferences) {
        setDashboardPreferences({
          ...DEFAULT_DASHBOARD_PREFERENCES,
          preset: storedDashboardPreferences.preset || 'custom',
          mode: storedDashboardPreferences.mode || DEFAULT_DASHBOARD_PREFERENCES.mode,
          sections: {
            ...DEFAULT_DASHBOARD_PREFERENCES.sections,
            ...(storedDashboardPreferences.sections || {}),
          },
          blocks: {
            ...DEFAULT_DASHBOARD_PREFERENCES.blocks,
            ...(storedDashboardPreferences.blocks || {}),
          },
        });
        return;
      }

      const saved = localStorage.getItem(dashboardPreferencesStorageKey);

      if (!saved) {
        setDashboardPreferences(DEFAULT_DASHBOARD_PREFERENCES);
        return;
      }

      const parsed = JSON.parse(saved);
      const migratedMode =
        parsed?.mode === 'fixed' ? 'operational' : parsed?.mode;

      setDashboardPreferences({
        ...DEFAULT_DASHBOARD_PREFERENCES,
        ...parsed,
        preset: parsed?.preset || 'custom',
        mode: migratedMode || DEFAULT_DASHBOARD_PREFERENCES.mode,
        sections: {
          ...DEFAULT_DASHBOARD_PREFERENCES.sections,
          ...(parsed?.sections || {}),
        },
        blocks: {
          ...DEFAULT_DASHBOARD_PREFERENCES.blocks,
          ...(parsed?.blocks || {}),
        },
      });
    } catch {
      setDashboardPreferences(DEFAULT_DASHBOARD_PREFERENCES);
    }
  }, [user, dashboardPreferencesStorageKey, storedDashboardPreferences]);

  useEffect(() => {
    if (!user) {
      setShowFinancialValues(true);
      return;
    }

    try {
      localStorage.removeItem('dashboard_show_financial_values');
      const savedPreference = localStorage.getItem(financialVisibilityStorageKey);
      setShowFinancialValues(savedPreference !== 'false');
    } catch {
      setShowFinancialValues(true);
    }
  }, [user, financialVisibilityStorageKey]);

  useEffect(() => {
    if (!user) {
      setCompletedDailyGoals([]);
      return;
    }

    try {
      const saved = localStorage.getItem(dailyGoalsStorageKey);
      const parsed = saved ? JSON.parse(saved) : [];
      setCompletedDailyGoals(Array.isArray(parsed) ? parsed : []);
    } catch {
      setCompletedDailyGoals([]);
    }
  }, [user, dailyGoalsStorageKey]);

  useEffect(() => {
    if (user) loadDashboard();
  }, [user]);

  useEffect(() => {
    if (!user || !showMonthlySummary || clinicBranding.name) return;

    const loadClinicBranding = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${backendUrl}/api/clinic-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Configurações indisponíveis');
        const settings = await response.json();
        const logoData = String(settings?.logo_data || '').trim();
        const logoFilename = String(settings?.logo_filename || '').toLowerCase();
        const logoMimeType = logoFilename.endsWith('.svg')
          ? 'image/svg+xml'
          : logoFilename.endsWith('.webp')
          ? 'image/webp'
          : logoFilename.endsWith('.gif')
          ? 'image/gif'
          : logoFilename.endsWith('.jpg') || logoFilename.endsWith('.jpeg')
          ? 'image/jpeg'
          : 'image/png';
        const storedLogoUrl = logoData
          ? logoData.startsWith('data:')
            ? logoData
            : `data:${logoMimeType};base64,${logoData}`
          : '';
        const rawLogoUrl = storedLogoUrl || (settings?.logo_filename
          ? `/api/settings/logo/${encodeURIComponent(settings.logo_filename)}`
          : settings?.logo_url || '');
        setClinicBranding({
          name:
            settings?.clinic_name ||
            user?.clinic_name ||
            user?.full_name ||
            'Clínica Odontológica',
          logoUrl: rawLogoUrl
            ? rawLogoUrl.startsWith('http') || rawLogoUrl.startsWith('data:')
              ? rawLogoUrl
              : `${backendUrl}${rawLogoUrl}`
            : '',
        });
      } catch {
        setClinicBranding({
          name: user?.clinic_name || user?.full_name || 'Clínica Odontológica',
          logoUrl: '',
        });
      }
    };

    loadClinicBranding();
  }, [backendUrl, clinicBranding.name, showMonthlySummary, user]);

  const todayAppointments = useMemo(() => {
    const today = localDateKey();
    return data.appointments
      .filter((item) => appointmentDateKey(item) === today)
      .sort((a, b) => appointmentTime(a).localeCompare(appointmentTime(b)));
  }, [data.appointments]);

  const currentMonthStart = useMemo(() => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }, []);

  const newPatientsThisMonth = useMemo(
    () =>
      data.patients.filter((patient) => {
        const createdAt = safeDate(patient?.created_at);
        return createdAt && createdAt >= currentMonthStart;
      }).length,
    [data.patients, currentMonthStart]
  );

  const monthlySummary = useMemo(() => {
    const [year, month] = selectedSummaryMonth.split('-').map(Number);
    const start = new Date(year, month - 1, 1);
    const previousStart = new Date(year, month - 2, 1);
    const previousMonthKey = monthKey(previousStart);
    const label = start.toLocaleDateString('pt-BR', {
      month: 'long',
      year: 'numeric',
    });

    const valueMonthKey = (value) => {
      if (!value) return '';
      if (typeof value === 'string' && /^\d{4}-\d{2}/.test(value)) return value.slice(0, 7);
      const date = safeDate(value);
      return date ? monthKey(date) : '';
    };
    const isCompleted = (appointment) => {
      const status = normalizedStatus(appointment?.status);
      return status.includes('realiz') || status.includes('conclu');
    };
    const isMissed = (appointment) => {
      const status = normalizedStatus(appointment?.status);
      return status.includes('falt') || status.includes('nao compareceu') || status.includes('no_show');
    };
    const patientKey = (appointment) =>
      String(
        appointment?.patient_id ||
          appointment?.patient?.id ||
          appointment?.patient_name ||
          appointment?.patient?.full_name ||
          ''
      )
        .trim()
        .toLocaleLowerCase('pt-BR');

    const summarize = (periodKey) => {
      const appointments = data.appointments.filter((appointment) =>
        valueMonthKey(
          appointment?.appointment_date ||
            appointment?.date ||
            appointment?.start_date ||
            appointment?.start
        ) === periodKey
      );
      const completed = appointments.filter(isCompleted);
      const missed = appointments.filter(isMissed);
      const comparable = completed.length + missed.length;
      return {
        patients: new Set(completed.map(patientKey).filter(Boolean)).size,
        appointments: completed.length,
        newPatients: data.patients.filter((patient) =>
          valueMonthKey(patient?.created_at) === periodKey
        ).length,
        attendanceRate: comparable > 0 ? Math.round((completed.length / comparable) * 100) : null,
      };
    };

    const current = summarize(selectedSummaryMonth);
    const previous = summarize(previousMonthKey);
    const patientChange =
      previous.patients > 0
        ? Math.round(((current.patients - previous.patients) / previous.patients) * 100)
        : null;

    return { ...current, label, patientChange };
  }, [data.appointments, data.patients, selectedSummaryMonth]);

  const createMonthlySummaryImage = async () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1350;
    const context = canvas.getContext('2d');
    const gradient = context.createLinearGradient(0, 0, 1080, 1350);
    gradient.addColorStop(0, '#6d28d9');
    gradient.addColorStop(0.55, '#4f46e5');
    gradient.addColorStop(1, '#312e81');
    context.fillStyle = gradient;
    context.fillRect(0, 0, 1080, 1350);

    context.fillStyle = 'rgba(255,255,255,0.09)';
    context.beginPath();
    context.arc(930, 130, 280, 0, Math.PI * 2);
    context.fill();
    context.beginPath();
    context.arc(120, 1250, 330, 0, Math.PI * 2);
    context.fill();

    const drawLogo = async () => {
      if (!clinicBranding.logoUrl) return;
      try {
        const response = await fetch(clinicBranding.logoUrl);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const image = new Image();
        await new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = reject;
          image.src = objectUrl;
        });
        context.save();
        roundedRect(context, 80, 70, 112, 112, 24);
        context.clip();
        context.fillStyle = '#ffffff';
        context.fillRect(80, 70, 112, 112);
        const scale = Math.min(96 / image.width, 96 / image.height);
        context.drawImage(
          image,
          136 - (image.width * scale) / 2,
          126 - (image.height * scale) / 2,
          image.width * scale,
          image.height * scale
        );
        context.restore();
        URL.revokeObjectURL(objectUrl);
      } catch {
        // A imagem continua válida mesmo quando a logomarca não puder ser carregada.
      }
    };

    await drawLogo();
    const titleX = clinicBranding.logoUrl ? 220 : 80;
    context.fillStyle = '#ffffff';
    context.font = '700 38px Arial, sans-serif';
    context.fillText(clinicBranding.name || 'Clínica Odontológica', titleX, 125, 760);
    context.fillStyle = '#ddd6fe';
    context.font = '500 25px Arial, sans-serif';
    context.fillText('Nosso mês em números', titleX, 165);

    context.fillStyle = '#ffffff';
    context.font = '800 72px Arial, sans-serif';
    context.fillText(monthlySummary.label.charAt(0).toUpperCase() + monthlySummary.label.slice(1), 80, 305);
    context.fillStyle = '#e0e7ff';
    context.font = '400 29px Arial, sans-serif';
    context.fillText('Cuidado, presença e crescimento em cada atendimento.', 80, 355);

    const metrics = [
      ['Pacientes atendidos', monthlySummary.patients],
      ['Consultas realizadas', monthlySummary.appointments],
      ['Novos pacientes', monthlySummary.newPatients],
      ['Taxa de comparecimento', monthlySummary.attendanceRate === null ? '—' : `${monthlySummary.attendanceRate}%`],
    ];
    metrics.forEach(([label, value], index) => {
      const column = index % 2;
      const row = Math.floor(index / 2);
      const x = 80 + column * 470;
      const y = 455 + row * 285;
      context.fillStyle = 'rgba(255,255,255,0.13)';
      roundedRect(context, x, y, 430, 235, 30);
      context.fill();
      context.fillStyle = '#ffffff';
      context.font = '800 78px Arial, sans-serif';
      context.fillText(String(value), x + 36, y + 105);
      context.fillStyle = '#e0e7ff';
      context.font = '600 25px Arial, sans-serif';
      context.fillText(label, x + 36, y + 165, 360);
    });

    context.fillStyle = '#ffffff';
    context.font = '700 31px Arial, sans-serif';
    context.fillText('Odontologia organizada. Resultados que aparecem.', 80, 1115);
    context.fillStyle = '#c7d2fe';
    context.font = '500 23px Arial, sans-serif';
    context.fillText('Resumo gerado pelo OdontoAssist AI', 80, 1190);
    context.fillStyle = '#ffffff';
    context.font = '800 30px Arial, sans-serif';
    context.textAlign = 'right';
    context.fillText('OdontoAssist AI', 1000, 1275);
    context.textAlign = 'left';

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Não foi possível gerar a imagem.'))),
        'image/png',
        1
      );
    });
  };

  const exportMonthlySummary = async ({ share = false } = {}) => {
    try {
      setExportingSummary(true);
      const blob = await createMonthlySummaryImage();
      const filename = `resumo-${selectedSummaryMonth}-odontoassist.png`;
      const file = new File([blob], filename, { type: 'image/png' });

      if (share && navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Resumo de ${monthlySummary.label}`,
          text: 'Nosso mês em números, com o OdontoAssist AI.',
        });
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      showToast?.(
        share ? 'O compartilhamento direto não está disponível. A imagem foi baixada.' : 'Imagem baixada com sucesso.',
        'success'
      );
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Erro ao gerar resumo mensal:', error);
        showToast?.('Não foi possível gerar a imagem do resumo mensal.', 'error');
      }
    } finally {
      setExportingSummary(false);
    }
  };

  const confirmedToday = todayAppointments.filter((item) =>
    normalizedStatus(item?.status).includes('confirm')
  ).length;
  const cancelledToday = todayAppointments.filter((item) =>
    normalizedStatus(item?.status).includes('cancel')
  ).length;
  const completedToday = todayAppointments.filter((item) => {
    const status = normalizedStatus(item?.status);
    return status.includes('conclu') || status.includes('realiz');
  }).length;
  const inProgressToday = todayAppointments.filter((item) => {
    const status = normalizedStatus(item?.status);
    return status.includes('atendimento') || status.includes('em_atendimento');
  }).length;
  const pendingConfirmations = todayAppointments.filter((item) => {
    const status = normalizedStatus(item?.status);
    return !status.includes('confirm') && !status.includes('cancel') && !status.includes('conclu');
  }).length;

  const activeTreatments = data.treatments.filter((item) => {
    const status = normalizedStatus(item?.status);
    return status.includes('andamento') || status.includes('ativo') || status.includes('in_progress');
  }).length;

  const finance = data.financial || {};
  const financialHistory = data.financialHistory || {};
  const monthlyRevenueHistory = Array.isArray(financialHistory.monthly_revenue)
    ? financialHistory.monthly_revenue
    : [];
  const comparison = finance.comparison || {};
  const cashForecast = finance.cash_forecast || {};
  const aligners = finance.aligner_summary || {};
  const overdue = Number(finance.total_overdue || 0);
  const pending = Number(finance.total_pending || 0);
  const collectionRate = Number(finance.collection_rate || 0);
  const lowStock = Number(data.inventory?.low_stock_items || 0);
  const cashProjection = finance.cash_projection || {};

  const hasTodayAgendaBase = todayAppointments.length > 0;
  const confirmationRate = hasTodayAgendaBase
    ? (confirmedToday / todayAppointments.length) * 100
    : null;
  const executionRate = hasTodayAgendaBase
    ? ((completedToday + inProgressToday) / todayAppointments.length) * 100
    : null;
  const hasFinancialBase =
    Number(finance.total_invoiced || 0) > 0 ||
    Number(finance.total_received || 0) > 0 ||
    Number(finance.total_pending || 0) > 0;

  const agendaScore = hasTodayAgendaBase
    ? Math.max(0, Math.min(100, 100 - pendingConfirmations * 8 - cancelledToday * 12))
    : null;

  const overdueReference = Math.max(Number(finance.total_invoiced || 0), 1);
  const overduePressure = Math.min(70, (overdue / overdueReference) * 100);
  const financeScore = hasFinancialBase
    ? Math.max(0, Math.min(100, collectionRate - overduePressure))
    : null;

  const patientScore = data.patients.length > 0
    ? Math.max(
        60,
        Math.min(
          100,
          newPatientsThisMonth > 0 ? 85 + Math.min(newPatientsThisMonth, 15) : 70
        )
      )
    : null;

  const operationalScore = Math.max(0, Math.min(100, 100 - lowStock * 8));

  const scoreDimensions = [
    { value: agendaScore, weight: 0.3 },
    { value: financeScore, weight: 0.35 },
    { value: patientScore, weight: 0.2 },
    { value: operationalScore, weight: 0.15 },
  ].filter((item) => item.value !== null && item.value !== undefined);

  const totalScoreWeight = scoreDimensions.reduce((sum, item) => sum + item.weight, 0);
  const clinicScore = totalScoreWeight > 0
    ? Math.round(
        scoreDimensions.reduce(
          (sum, item) => sum + Number(item.value) * item.weight,
          0
        ) / totalScoreWeight
      )
    : null;

  const clinicScoreLabel =
    clinicScore === null
      ? 'Sem dados suficientes'
      : clinicScore >= 85
      ? 'Excelente'
      : clinicScore >= 70
      ? 'Boa'
      : clinicScore >= 50
      ? 'Atenção'
      : 'Crítica';

  const clinicScoreBreakdown = [
    {
      key: 'agenda',
      label: 'Agenda',
      score: agendaScore,
      weight: 30,
      icon: Calendar,
      description:
        agendaScore === null
          ? 'Sem atendimentos hoje para calcular a dimensão.'
          : pendingConfirmations > 0 || cancelledToday > 0
          ? `${pendingConfirmations} confirmação(ões) pendente(s) e ${cancelledToday} cancelamento(s).`
          : 'Agenda do dia sem pendências relevantes.',
    },
    {
      key: 'financial',
      label: 'Financeiro',
      score: financeScore,
      weight: 35,
      icon: DollarSign,
      description:
        financeScore === null
          ? 'Sem movimentação financeira suficiente no período.'
          : overdue > 0
          ? `${maskedMoney(overdue)} em cobranças vencidas reduzem a pontuação.`
          : pending > 0
          ? `${maskedMoney(pending)} ainda aguardam recebimento.`
          : 'Financeiro sem valores vencidos identificados.',
    },
    {
      key: 'patients',
      label: 'Pacientes',
      score: patientScore,
      weight: 20,
      icon: Users,
      description:
        patientScore === null
          ? 'Nenhum paciente cadastrado para análise.'
          : newPatientsThisMonth > 0
          ? `${newPatientsThisMonth} novo(s) paciente(s) no mês.`
          : 'Nenhum novo paciente registrado neste mês.',
    },
    {
      key: 'operational',
      label: 'Operacional',
      score: operationalScore,
      weight: 15,
      icon: Package,
      description:
        lowStock > 0
          ? `${lowStock} item(ns) com estoque baixo reduzem a pontuação.`
          : 'Estoque sem alertas relevantes.',
    },
  ];

  const clinicScoreLosses = clinicScoreBreakdown
    .filter((item) => item.score !== null && item.score !== undefined)
    .map((item) => ({
      ...item,
      loss: Math.max(
        0,
        Math.round(((100 - Number(item.score)) * item.weight) / 100)
      ),
    }))
    .sort((a, b) => b.loss - a.loss);

  const clinicScoreAvailableWeight = clinicScoreBreakdown
    .filter((item) => item.score !== null && item.score !== undefined)
    .reduce((sum, item) => sum + item.weight, 0);

  const agendaHealth =
    pendingConfirmations >= 5 || cancelledToday >= 3
      ? 'critical'
      : pendingConfirmations > 0 || cancelledToday > 0
      ? 'attention'
      : 'good';
  const financeHealth = !hasFinancialBase
    ? 'attention'
    : overdue > 0
    ? 'critical'
    : pending > 0
    ? 'attention'
    : 'good';
  const stockHealth = lowStock >= 5 ? 'critical' : lowStock > 0 ? 'attention' : 'good';
  const patientHealth = data.patients.length === 0
    ? 'attention'
    : newPatientsThisMonth > 0
    ? 'good'
    : 'attention';

  const alerts = useMemo(() => {
    const items = [];

    if (pendingConfirmations > 0) {
      items.push({
        type: 'warning',
        title: `${pendingConfirmations} consulta(s) aguardando confirmação hoje`,
        action: 'Abrir agenda',
        href: '/agenda',
      });
    } else if (todayAppointments.length > 0) {
      items.push({
        type: 'positive',
        title: 'Todos os agendamentos de hoje estão tratados',
        action: 'Ver agenda',
        href: '/agenda',
      });
    }

    if (overdue > 0) {
      items.push({
        type: 'critical',
        title: `${maskedMoney(overdue)} em valores vencidos`,
        action: 'Ver faturas',
        href: '/financeiro?tab=invoices&status=vencido',
      });
    } else {
      items.push({
        type: 'positive',
        title: 'Nenhum valor vencido identificado no período',
        action: 'Ver financeiro',
        href: '/financeiro?tab=reports',
      });
    }

    if (lowStock > 0) {
      items.push({
        type: lowStock >= 5 ? 'critical' : 'warning',
        title: `${lowStock} item(ns) com estoque baixo`,
        action: 'Abrir estoque',
        href: '/estoque',
      });
    }

    if (data.reminders.length > 0) {
      items.push({
        type: 'info',
        title: `${data.reminders.length} lembrete(s) programado(s) para hoje`,
        action: 'Ver lembretes',
        href: '/lembretes',
      });
    }

    return items.slice(0, 5);
  }, [data.reminders.length, lowStock, overdue, pendingConfirmations, todayAppointments.length]);

  const priorities = useMemo(() => {
    const items = [];

    if (pendingConfirmations > 0) {
      items.push({
        severity: pendingConfirmations >= 5 ? 'critical' : 'warning',
        title: `${pendingConfirmations} consulta(s) ainda aguardam confirmação`,
        detail: 'Confirme ou trate esses horários antes do início dos atendimentos.',
        href: '/agenda',
      });
    }

    if (overdue > 0) {
      items.push({
        severity: 'critical',
        title: `${maskedMoney(overdue)} em cobranças vencidas`,
        detail: 'Priorize a régua de cobrança e os contatos com maior atraso.',
        href: '/financeiro?tab=invoices&status=vencido',
      });
    }

    if (lowStock > 0) {
      items.push({
        severity: lowStock >= 5 ? 'critical' : 'warning',
        title: `${lowStock} item(ns) com estoque baixo`,
        detail: 'Revise os insumos necessários para os próximos atendimentos.',
        href: '/estoque',
      });
    }

    if (data.reminders.length > 0) {
      items.push({
        severity: 'info',
        title: `${data.reminders.length} lembrete(s) para hoje`,
        detail: 'Há compromissos administrativos ou clínicos que merecem acompanhamento.',
        href: '/lembretes',
      });
    }

    if (items.length === 0) {
      items.push({
        severity: 'positive',
        title: 'Nenhuma prioridade crítica identificada',
        detail: 'Agenda, financeiro, estoque e lembretes estão sob controle neste momento.',
        href: '/agenda',
      });
    }

    return items.slice(0, 5);
  }, [data.reminders.length, lowStock, overdue, pendingConfirmations]);

  const dailyInsights = useMemo(() => {
    const insights = [];
    const occupancyReference = Math.max(todayAppointments.length, 1);
    const confirmedRate = (confirmedToday / occupancyReference) * 100;

    if (todayAppointments.length > 0) {
      insights.push(
        `Hoje há ${todayAppointments.length} atendimento(s) na agenda, com ${confirmedRate.toLocaleString(
          'pt-BR',
          { maximumFractionDigits: 0 }
        )}% já confirmado(s).`
      );
    } else {
      insights.push('Não há atendimentos registrados para hoje.');
    }

    insights.push(
      `O mês registra ${maskedMoney(finance.total_received)} recebidos e ${money(
        cashForecast.next_30_days
      )} previstos para os próximos 30 dias.`
    );

    if (comparison?.available && comparison?.invoiced_change_percentage !== null) {
      const change = Number(comparison.invoiced_change_percentage || 0);
      insights.push(
        `O faturamento ${change >= 0 ? 'cresceu' : 'recuou'} ${Math.abs(change).toLocaleString(
          'pt-BR',
          { maximumFractionDigits: 1 }
        )}% em relação ao período anterior equivalente.`
      );
    }

    if (Number(aligners?.total_cases || 0) > 0) {
      insights.push(
        `Existem ${number(aligners.total_cases)} caso(s) de alinhadores, com ${number(
          aligners.in_progress
        )} em andamento.`
      );
    }

    return insights.slice(0, 4);
  }, [aligners, cashForecast.next_30_days, comparison, confirmedToday, finance.total_received, todayAppointments.length]);

  const predictiveData = useMemo(() => {
    const now = new Date();
    const todayKey = localDateKey(now);
    const patientKey = (item) =>
      String(
        item?.patient_id || item?.patient?.id || item?.patient_name || item?.patient?.full_name || ''
      )
        .trim()
        .toLowerCase();

    const historyByPatient = new Map();
    data.appointments.forEach((appointment) => {
      const key = patientKey(appointment);
      if (!key) return;
      const date = safeDate(
        appointment?.appointment_date || appointment?.date || appointment?.start_date || appointment?.start
      );
      if (!date || appointmentDateKey(appointment) >= todayKey) return;
      const current = historyByPatient.get(key) || { total: 0, missed: 0, cancelled: 0 };
      const status = normalizedStatus(appointment?.status);
      current.total += 1;
      if (status.includes('falt') || status.includes('no_show')) current.missed += 1;
      if (status.includes('cancel')) current.cancelled += 1;
      historyByPatient.set(key, current);
    });

    const noShowRisks = todayAppointments
      .map((appointment) => {
        const history = historyByPatient.get(patientKey(appointment)) || {
          total: 0,
          missed: 0,
          cancelled: 0,
        };
        const status = normalizedStatus(appointment?.status);
        let score = 10;
        if (!status.includes('confirm')) score += 30;
        if (history.total > 0) {
          score += (history.missed / history.total) * 45;
          score += (history.cancelled / history.total) * 20;
        }
        score = Math.max(5, Math.min(95, Math.round(score)));
        return {
          id: appointment?.id,
          name:
            appointment?.patient_name ||
            appointment?.patient?.full_name ||
            appointment?.title ||
            'Paciente',
          time: appointmentTime(appointment),
          score,
          history,
        };
      })
      .filter((item) => item.score >= 40)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);

    const latestAppointmentByPatient = new Map();
    data.appointments.forEach((appointment) => {
      const key = patientKey(appointment);
      const date = safeDate(
        appointment?.appointment_date || appointment?.date || appointment?.start_date || appointment?.start
      );
      if (!key || !date || date > now) return;
      const previous = latestAppointmentByPatient.get(key);
      if (!previous || date > previous) latestAppointmentByPatient.set(key, date);
    });

    const inactivePatients = data.patients
      .map((patient) => {
        const key = String(patient?.id || patient?._id || patient?.full_name || '').trim().toLowerCase();
        const lastDate = latestAppointmentByPatient.get(key);
        if (!lastDate) return null;
        const days = Math.floor((now - lastDate) / 86400000);
        if (days < 180) return null;
        return { id: patient?.id || patient?._id, name: patient?.full_name || patient?.name || 'Paciente', days };
      })
      .filter(Boolean)
      .sort((a, b) => b.days - a.days)
      .slice(0, 5);

    const activePatientKeys = new Set(data.patients.flatMap(patient =>
      [patient.id, patient.full_name].filter(Boolean).map(value => String(value).trim().toLowerCase())
    ));
    const stalledTreatments = data.treatments
      .map((treatment) => {
        const patientKey = String(treatment?.patient_id || treatment?.patient?.id || treatment?.patient_name || treatment?.patient?.full_name || '').trim().toLowerCase();
        if (!activePatientKeys.has(patientKey)) return null;
        const status = normalizedStatus(treatment?.status);
        if (!(status.includes('andamento') || status.includes('ativo') || status.includes('in_progress'))) return null;
        const reference = safeDate(treatment?.updated_at || treatment?.start_date || treatment?.created_at);
        if (!reference) return null;
        const days = Math.floor((now - reference) / 86400000);
        if (days < 90) return null;
        return {
          id: treatment?.id || treatment?._id,
          patient: treatment?.patient_name || treatment?.patient?.full_name || 'Paciente',
          procedure: treatment?.procedure || treatment?.title || 'Tratamento',
          days,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.days - a.days)
      .slice(0, 5);

    const weekdayCounts = Array(7).fill(0);
    const weekdayOccurrences = Array(7).fill(0);

    data.appointments.forEach((appointment) => {
      const date = safeDate(
        appointment?.appointment_date || appointment?.date || appointment?.start_date || appointment?.start
      );
      if (!date) return;
      const age = (now - date) / 86400000;
      if (age >= 0 && age <= 90) weekdayCounts[date.getDay()] += 1;
    });

    for (let offset = 0; offset <= 90; offset += 1) {
      const date = new Date(now);
      date.setHours(12, 0, 0, 0);
      date.setDate(date.getDate() - offset);
      weekdayOccurrences[date.getDay()] += 1;
    }

    const workingDays = [1, 2, 3, 4, 5];
    const weekdayAverage = workingDays.map((day) => ({
      day,
      count: weekdayCounts[day],
      average: weekdayOccurrences[day]
        ? weekdayCounts[day] / weekdayOccurrences[day]
        : 0,
    }));
    const maxAverage = Math.max(...weekdayAverage.map((item) => item.average), 0);
    const weekdayNames = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
    const weakestDay = weekdayAverage
      .map((item) => ({
        ...item,
        occupancy: maxAverage > 0
          ? Math.round((item.average / maxAverage) * 100)
          : null,
      }))
      .sort((a, b) => a.average - b.average)[0];

    const projected = Number(cashProjection.projected_month_revenue || cashProjection.projected_month_closing || 0);
    const projectionRange = projected > 0 ? { low: projected * 0.9, high: projected * 1.1 } : null;

    return { noShowRisks, inactivePatients, stalledTreatments, weakestDay: { ...weakestDay, label: weekdayNames[weakestDay.day] }, projectionRange };
  }, [cashProjection.projected_month_closing, cashProjection.projected_month_revenue, data.appointments, data.patients, data.treatments, todayAppointments]);

  const dailyGoals = useMemo(() => {
    const goals = [];

    if (pendingConfirmations > 0) {
      goals.push({
        id: 'confirm-appointments',
        title: `Confirmar ${pendingConfirmations} consulta(s)`,
        description: 'Revise os agendamentos de hoje que ainda aguardam confirmação.',
        icon: Calendar,
        href: '/agenda',
      });
    } else {
      goals.push({
        id: 'review-agenda',
        title: 'Revisar a agenda de hoje',
        description:
          todayAppointments.length > 0
            ? `${todayAppointments.length} atendimento(s) programado(s) para hoje.`
            : 'Verifique oportunidades para preencher horários disponíveis.',
        icon: Calendar,
        href: '/agenda',
      });
    }

    if (overdue > 0) {
      goals.push({
        id: 'overdue-collections',
        title: 'Tratar cobranças vencidas',
        description: `${maskedMoney(overdue)} aguardando regularização.`,
        icon: DollarSign,
        href: '/financeiro?tab=invoices&status=vencido',
      });
    } else {
      goals.push({
        id: 'review-financial',
        title: 'Revisar o financeiro do dia',
        description: `${maskedMoney(cashForecast.next_7_days)} previstos para os próximos 7 dias.`,
        icon: Wallet,
        href: '/financeiro?tab=reports',
      });
    }

    if (predictiveData.inactivePatients.length > 0) {
      goals.push({
        id: 'recover-patients',
        title: `Contatar ${predictiveData.inactivePatients.length} paciente(s) sem retorno`,
        description: 'Priorize pacientes com maior tempo desde o último atendimento.',
        icon: UserRoundPlus,
        href: '/prontuarios?modo=lista&filter=inactive',
      });
    } else if (predictiveData.stalledTreatments.length > 0) {
      goals.push({
        id: 'resume-treatments',
        title: `Acompanhar ${predictiveData.stalledTreatments.length} tratamento(s) parado(s)`,
        description: 'Revise os casos que podem voltar à produção clínica.',
        icon: Activity,
        href: '/prontuarios?modo=lista&tab=treatments&filter=stalled',
      });
    } else {
      goals.push({
        id: 'review-patients',
        title: 'Revisar oportunidades com pacientes',
        description: 'Acompanhe retornos, tratamentos e próximos contatos.',
        icon: Users,
        href: '/prontuarios?modo=lista',
      });
    }

    if (lowStock > 0) {
      goals.push({
        id: 'review-stock',
        title: `Revisar ${lowStock} item(ns) com estoque baixo`,
        description: 'Confirme os insumos necessários para os próximos atendimentos.',
        icon: Package,
        href: '/estoque',
      });
    } else if (data.reminders.length > 0) {
      goals.push({
        id: 'review-reminders',
        title: `Concluir ${data.reminders.length} lembrete(s) do dia`,
        description: 'Verifique compromissos administrativos e clínicos pendentes.',
        icon: Bell,
        href: '/lembretes',
      });
    } else {
      goals.push({
        id: 'review-indicators',
        title: 'Revisar os principais indicadores',
        description: 'Confirme se agenda, financeiro e operação permanecem sob controle.',
        icon: ListChecks,
        href: '/dashboard',
      });
    }

    return goals.slice(0, 4);
  }, [
    cashForecast.next_7_days,
    data.reminders.length,
    lowStock,
    overdue,
    pendingConfirmations,
    predictiveData.inactivePatients.length,
    predictiveData.stalledTreatments.length,
    todayAppointments.length,
  ]);

  const completedDailyGoalsCount = dailyGoals.filter((goal) =>
    completedDailyGoals.includes(goal.id)
  ).length;

  const dailyGoalsProgress =
    dailyGoals.length > 0
      ? Math.round((completedDailyGoalsCount / dailyGoals.length) * 100)
      : 0;

  const toggleDailyGoal = (goalId) => {
    setCompletedDailyGoals((current) => {
      const next = current.includes(goalId)
        ? current.filter((item) => item !== goalId)
        : [...current, goalId];

      try {
        localStorage.setItem(dailyGoalsStorageKey, JSON.stringify(next));
      } catch {
        // Mantém o progresso somente durante a sessão.
      }

      return next;
    });
  };

  const quickLinks = [
    { label: 'Nova consulta', icon: Calendar, href: '/agenda' },
    { label: 'Novo paciente', icon: Users, href: '/prontuarios' },
    { label: 'Financeiro', icon: DollarSign, href: '/financeiro' },
    { label: 'Alinhadores', icon: Smile, href: '/alinhadores' },
  ];

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  })();

  const firstName = String(user?.full_name || 'Doutor').trim().split(' ')[0];
  const formattedToday = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  const openWhatsAppWeb = async () => {
    const isMobile =
      /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      window.matchMedia('(max-width: 768px)').matches;

    if (isMobile) {
      showToast?.('No celular, use esse atalho dentro do prontuário do paciente.', 'info');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/extension/session-ticket?source=dashboard`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (!response.ok || !result?.ticket) throw new Error('Ticket não gerado');

      const url = new URL('https://web.whatsapp.com');
      url.searchParams.set('odontoassist_source', 'dashboard');
      url.searchParams.set('ext_ticket', result.ticket);
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error(error);
      showToast?.('Não foi possível abrir a integração com o WhatsApp.', 'error');
    }
  };

  const generatedAtLabel = (() => {
    const parsed = safeDate(data.generatedAt);
    if (!parsed) return 'Horário ainda não disponível';

    return parsed.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  })();

  const intelligentFocus = (() => {
    if (Number(overdue || 0) > 0) {
      return {
        key: 'management',
        title: 'Atenção financeira',
        description: `${maskedMoney(overdue)} em cobranças vencidas exige acompanhamento.`,
      };
    }

    if (predictiveData.noShowRisks.length > 0 || pendingConfirmations > 0) {
      return {
        key: 'today',
        title: 'Prioridade na agenda de hoje',
        description:
          predictiveData.noShowRisks.length > 0
            ? `${predictiveData.noShowRisks.length} agendamento(s) com maior risco de falta.`
            : `${pendingConfirmations} agendamento(s) ainda aguardam confirmação.`,
      };
    }

    if (
      predictiveData.inactivePatients.length > 0 ||
      predictiveData.stalledTreatments.length > 0
    ) {
      return {
        key: 'intelligence',
        title: 'Oportunidades de recuperação',
        description:
          `${predictiveData.inactivePatients.length} paciente(s) sem retorno e ` +
          `${predictiveData.stalledTreatments.length} tratamento(s) parado(s).`,
      };
    }

    return {
      key: 'today',
      title: 'Operação sob controle',
      description: 'Nenhum alerta crítico foi identificado. O foco permanece na agenda do dia.',
    };
  })();

  const dashboardModes = {
    intelligent: {
      label: 'Inteligente',
      shortDescription: 'Prioridade automática',
      icon: Sparkles,
    },
    operational: {
      label: 'Operacional',
      shortDescription: 'Agenda e rotina',
      icon: BriefcaseBusiness,
    },
    financial: {
      label: 'Financeiro',
      shortDescription: 'Caixa e recebimentos',
      icon: DollarSign,
    },
    growth: {
      label: 'Crescimento',
      shortDescription: 'Pacientes e oportunidades',
      icon: UserRoundPlus,
    },
  };

  const activeDashboardMode =
    dashboardModes[dashboardPreferences.mode] || dashboardModes.intelligent;

  const adaptiveFocus = (() => {
    if (dashboardPreferences.mode === 'financial') {
      return {
        title: 'Foco financeiro',
        description:
          overdue > 0
            ? `${maskedMoney(overdue)} em cobranças vencidas devem ser priorizados.`
            : `${maskedMoney(cashForecast.next_30_days)} previstos para os próximos 30 dias.`,
      };
    }

    if (dashboardPreferences.mode === 'growth') {
      return {
        title: 'Foco em crescimento',
        description:
          predictiveData.inactivePatients.length > 0 ||
          predictiveData.stalledTreatments.length > 0
            ? `${predictiveData.inactivePatients.length} paciente(s) sem retorno e ${predictiveData.stalledTreatments.length} tratamento(s) parado(s) representam oportunidades.`
            : `${newPatientsThisMonth} novo(s) paciente(s) foram registrados neste mês.`,
      };
    }

    if (dashboardPreferences.mode === 'operational') {
      return {
        title: 'Foco operacional',
        description:
          todayAppointments.length > 0
            ? `${todayAppointments.length} atendimento(s) hoje, com ${pendingConfirmations} confirmação(ões) pendente(s).`
            : 'A agenda de hoje não possui atendimentos registrados.',
      };
    }

    return {
      title: `Foco inteligente: ${intelligentFocus.title}`,
      description: intelligentFocus.description,
    };
  })();

  const sectionOrder = (() => {
    const base = {
      today: 'order-1',
      management: 'order-7',
      intelligence: 'order-8',
    };

    if (dashboardPreferences.mode === 'financial') {
      return {
        ...base,
        management: 'order-6',
        intelligence: 'order-7',
      };
    }

    if (dashboardPreferences.mode === 'growth') {
      return {
        ...base,
        intelligence: 'order-6',
        management: 'order-7',
      };
    }

    if (dashboardPreferences.mode === 'operational') {
      return {
        ...base,
        management: 'order-6',
        intelligence: 'order-7',
      };
    }

    return {
      ...base,
      management:
        intelligentFocus.key === 'management' ? 'order-6' : 'order-7',
      intelligence:
        intelligentFocus.key === 'intelligence' ? 'order-6' : 'order-7',
    };
  })();

  const executiveAssistant = (() => {
    const alerts = [];
    const recommendations = [];

    if (Number(overdue || 0) > 0) {
      alerts.push({
        level: 'critical',
        title: 'Cobranças vencidas',
        description: `${maskedMoney(overdue)} aguardando regularização.`,
      });
      recommendations.push({
        label: 'Revisar cobranças vencidas',
        action: () => navigateToFinancial('invoices', { status: 'vencido' }),
      });
    }

    if (pendingConfirmations > 0) {
      alerts.push({
        level: 'warning',
        title: 'Confirmações pendentes',
        description: `${pendingConfirmations} agendamento(s) ainda não confirmado(s).`,
      });
      recommendations.push({
        label: 'Revisar agenda de hoje',
        action: () => navigate('/agenda'),
      });
    }

    if (predictiveData.noShowRisks.length > 0) {
      alerts.push({
        level: 'warning',
        title: 'Risco de faltas',
        description: `${predictiveData.noShowRisks.length} agendamento(s) com maior risco de ausência.`,
      });
    }

    if (predictiveData.inactivePatients.length > 0) {
      alerts.push({
        level: 'opportunity',
        title: 'Pacientes sem retorno',
        description: `${predictiveData.inactivePatients.length} paciente(s) podem ser recuperado(s).`,
      });
      recommendations.push({
        label: 'Ver pacientes sem retorno',
        action: () => navigateToPatients('inactive'),
      });
    }

    if (predictiveData.stalledTreatments.length > 0) {
      alerts.push({
        level: 'opportunity',
        title: 'Tratamentos parados',
        description: `${predictiveData.stalledTreatments.length} tratamento(s) precisam de acompanhamento.`,
      });
      recommendations.push({
        label: 'Ver tratamentos parados',
        action: () => navigateToTreatments('stalled'),
      });
    }

    if (todayAppointments.length === 0) {
      alerts.push({
        level: 'warning',
        title: 'Agenda sem atendimentos',
        description: 'Não existem agendamentos previstos para hoje.',
      });
      recommendations.push({
        label: 'Abrir agenda',
        action: () => navigate('/agenda'),
      });
    }

    if (alerts.length === 0) {
      alerts.push({
        level: 'healthy',
        title: 'Operação sob controle',
        description: 'Nenhuma pendência crítica foi identificada neste momento.',
      });
    }

    if (recommendations.length === 0) {
      recommendations.push({
        label: 'Acompanhar indicadores',
        action: () => navigateToFinancial('reports'),
      });
    }

    const hasCritical = alerts.some((item) => item.level === 'critical');
    const hasWarning = alerts.some((item) => item.level === 'warning');
    const hasOpportunity = alerts.some((item) => item.level === 'opportunity');

    let headline = 'Sua clínica está operando normalmente.';
    let summary =
      'Os principais indicadores não mostram necessidade de intervenção imediata.';

    if (hasCritical) {
      headline = 'Há uma prioridade financeira que exige atenção.';
      summary =
        'Comece pelas cobranças vencidas e depois revise as pendências operacionais.';
    } else if (hasWarning) {
      headline = 'A operação de hoje merece acompanhamento.';
      summary =
        'Confirmações, faltas ou ocupação da agenda podem exigir ação da equipe.';
    } else if (hasOpportunity) {
      headline = 'Existem oportunidades de recuperação.';
      summary =
        'Pacientes sem retorno e tratamentos parados podem gerar nova produção clínica.';
    }

    return {
      alerts: alerts.slice(0, 3),
      recommendations: recommendations.slice(0, 3),
      headline,
      summary,
    };
  })();

  const managementConsultant = useMemo(() => {
    const periodDays = Number(consultantPeriod);
    const periodFinancial = data.managementPeriods?.[consultantPeriod] || {};
    const periodComparison = periodFinancial.comparison || {};
    const insights = [];
    const actions = [];

    const periodEnd = new Date();
    periodEnd.setHours(23, 59, 59, 999);
    const periodStart = new Date(periodEnd);
    periodStart.setDate(periodStart.getDate() - Math.max(periodDays - 1, 0));
    periodStart.setHours(0, 0, 0, 0);

    const appointmentDate = (appointment) =>
      safeDate(
        appointment?.appointment_date ||
          appointment?.date ||
          appointment?.start_date ||
          appointment?.start
      );

    const periodAppointments = data.appointments.filter((appointment) => {
      const date = appointmentDate(appointment);
      return date && date >= periodStart && date <= periodEnd;
    });

    const previousStart = new Date(periodStart);
    previousStart.setDate(previousStart.getDate() - periodDays);
    const previousEnd = new Date(periodStart);
    previousEnd.setMilliseconds(-1);

    const previousAppointments = data.appointments.filter((appointment) => {
      const date = appointmentDate(appointment);
      return date && date >= previousStart && date <= previousEnd;
    });

    const isMissed = (appointment) => {
      const status = normalizedStatus(appointment?.status);
      return status.includes('falt') || status.includes('no_show');
    };

    const periodMissed = periodAppointments.filter(isMissed).length;
    const previousMissed = previousAppointments.filter(isMissed).length;

    const appointmentChange =
      previousAppointments.length > 0
        ? ((periodAppointments.length - previousAppointments.length) /
            previousAppointments.length) *
          100
        : null;

    const noShowRate =
      periodAppointments.length > 0
        ? (periodMissed / periodAppointments.length) * 100
        : null;
    const previousNoShowRate =
      previousAppointments.length > 0
        ? (previousMissed / previousAppointments.length) * 100
        : null;

    const weekdayCounts = Array(7).fill(0);
    periodAppointments.forEach((appointment) => {
      const date = appointmentDate(appointment);
      if (date) weekdayCounts[date.getDay()] += 1;
    });

    const weekdayOccurrences = Array(7).fill(0);
    for (
      let cursor = new Date(periodStart);
      cursor <= periodEnd;
      cursor.setDate(cursor.getDate() + 1)
    ) {
      weekdayOccurrences[cursor.getDay()] += 1;
    }

    const weekdayNames = [
      'domingo',
      'segunda-feira',
      'terça-feira',
      'quarta-feira',
      'quinta-feira',
      'sexta-feira',
      'sábado',
    ];

    const workingDayAverages = [1, 2, 3, 4, 5].map((day) => ({
      day,
      average:
        weekdayOccurrences[day] > 0
          ? weekdayCounts[day] / weekdayOccurrences[day]
          : 0,
    }));

    const maxWeekdayAverage = Math.max(
      ...workingDayAverages.map((item) => item.average),
      0
    );

    const weakestPeriodDay = workingDayAverages
      .map((item) => ({
        ...item,
        occupancy:
          maxWeekdayAverage > 0
            ? Math.round((item.average / maxWeekdayAverage) * 100)
            : null,
      }))
      .sort((a, b) => a.average - b.average)[0];

    const revenueChange = periodComparison?.available
      ? Number(periodComparison.invoiced_change_percentage || 0)
      : null;
    const receivedChange = periodComparison?.available
      ? Number(periodComparison.received_change_percentage || 0)
      : null;
    const periodInvoiced = Number(periodFinancial.total_invoiced || 0);
    const periodReceived = Number(periodFinancial.total_received || 0);
    const periodOverdue = Number(periodFinancial.total_overdue || 0);
    const periodPending = Number(periodFinancial.total_pending || 0);
    const periodCollectionRate = Number(periodFinancial.collection_rate || 0);
    const periodOperatingResult = Number(periodFinancial.operating_result || 0);
    const periodOperatingMargin = Number(periodFinancial.operating_margin || 0);

    if (revenueChange !== null) {
      insights.push({
        level: revenueChange <= -10 ? 'critical' : revenueChange >= 10 ? 'positive' : 'neutral',
        area: 'Faturamento',
        title: `O faturamento ${revenueChange >= 0 ? 'cresceu' : 'recuou'} ${Math.abs(
          revenueChange
        ).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% em ${periodDays} dias`,
        description:
          'A comparação usa o intervalo anterior de mesma duração.',
        impact:
          revenueChange <= -10
            ? 'Risco de redução da receita recorrente'
            : revenueChange >= 10
            ? 'Oportunidade de consolidar o crescimento'
            : 'Variação dentro de uma faixa moderada',
      });
      if (revenueChange <= -10) {
        actions.push({
          label: 'Analisar relatório financeiro',
          action: () => navigateToFinancial('reports'),
        });
      }
    }

    if (periodOverdue > 0) {
      const overdueRatio =
        periodInvoiced > 0 ? (periodOverdue / periodInvoiced) * 100 : null;
      insights.push({
        level: overdueRatio !== null && overdueRatio >= 15 ? 'critical' : 'warning',
        area: 'Inadimplência',
        title:
          overdueRatio !== null
            ? `${overdueRatio.toLocaleString('pt-BR', {
                maximumFractionDigits: 1,
              })}% do faturado no período está vencido`
            : `${maskedMoney(periodOverdue)} estão vencidos`,
        description:
          'Valores vencidos pressionam o caixa e devem ser priorizados por tempo de atraso.',
        impact: `${maskedMoney(periodOverdue)} com recebimento em risco`,
      });
      actions.push({
        label: 'Tratar cobranças vencidas',
        action: () => navigateToFinancial('invoices', { status: 'vencido' }),
      });
    } else if (periodInvoiced > 0 || periodReceived > 0) {
      insights.push({
        level: 'positive',
        area: 'Inadimplência',
        title: `Nenhuma cobrança vencida foi identificada em ${periodDays} dias`,
        description:
          'A ausência de valores vencidos indica boa regularidade financeira.',
        impact: 'Fluxo de caixa sem pressão por atraso',
      });
    }

    if (periodReceived > 0 || Number(periodFinancial.total_expenses_paid || 0) > 0) {
      insights.push({
        level:
          periodOperatingResult < 0
            ? 'critical'
            : periodOperatingMargin < 15
            ? 'warning'
            : 'positive',
        area: 'Resultado',
        title:
          periodOperatingResult < 0
            ? `O resultado operacional foi negativo em ${maskedMoney(
                Math.abs(periodOperatingResult)
              )}`
            : `O resultado operacional foi de ${maskedMoney(periodOperatingResult)}`,
        description: `A margem operacional foi de ${periodOperatingMargin.toLocaleString(
          'pt-BR',
          { maximumFractionDigits: 1 }
        )}%.`,
        impact:
          periodOperatingResult < 0
            ? 'Despesas superaram os recebimentos'
            : periodOperatingMargin < 15
            ? 'Margem reduzida para absorver imprevistos'
            : 'Geração positiva de caixa operacional',
      });
    }

    if (weakestPeriodDay?.occupancy !== null && weakestPeriodDay?.occupancy !== undefined) {
      insights.push({
        level: Number(weakestPeriodDay.occupancy) < 60 ? 'warning' : 'neutral',
        area: 'Agenda',
        title: `${weekdayNames[weakestPeriodDay.day]} teve a menor ocupação em ${periodDays} dias`,
        description: `A média equivale a ${Number(
          weakestPeriodDay.occupancy
        ).toLocaleString('pt-BR', {
          maximumFractionDigits: 0,
        })}% do dia mais movimentado.`,
        impact:
          Number(weakestPeriodDay.occupancy) < 60
            ? 'Horários ociosos podem limitar a produção'
            : 'Distribuição da agenda relativamente equilibrada',
      });
    }

    if (appointmentChange !== null && Math.abs(appointmentChange) >= 10) {
      insights.push({
        level: appointmentChange < 0 ? 'warning' : 'positive',
        area: 'Movimento',
        title: `O volume de atendimentos ${
          appointmentChange >= 0 ? 'cresceu' : 'recuou'
        } ${Math.abs(appointmentChange).toLocaleString('pt-BR', {
          maximumFractionDigits: 1,
        })}%`,
        description: `Foram ${periodAppointments.length} agendamento(s), contra ${previousAppointments.length} no intervalo anterior.`,
        impact:
          appointmentChange < 0
            ? 'Menor utilização da capacidade clínica'
            : 'Maior utilização da agenda',
      });
    }

    if (noShowRate !== null) {
      const noShowChange =
        previousNoShowRate !== null ? noShowRate - previousNoShowRate : null;
      insights.push({
        level: noShowRate >= 10 ? 'critical' : noShowRate >= 5 ? 'warning' : 'positive',
        area: 'Comparecimento',
        title: `A taxa de faltas foi de ${noShowRate.toLocaleString('pt-BR', {
          maximumFractionDigits: 1,
        })}%`,
        description:
          noShowChange !== null
            ? `Variação de ${noShowChange >= 0 ? '+' : ''}${noShowChange.toLocaleString(
                'pt-BR',
                { maximumFractionDigits: 1 }
              )} ponto(s) percentual(is) contra o período anterior.`
            : `${periodMissed} falta(s) entre ${periodAppointments.length} agendamento(s).`,
        impact:
          noShowRate >= 5
            ? 'Perda potencial de produtividade e receita'
            : 'Comparecimento dentro de nível saudável',
      });
      if (noShowRate >= 5) {
        actions.push({
          label: 'Revisar agenda e confirmações',
          action: () => navigate('/agenda'),
        });
      }
    }

    if (periodPending > 0 && periodCollectionRate < 80) {
      insights.push({
        level: periodCollectionRate < 60 ? 'critical' : 'warning',
        area: 'Recebimentos',
        title: `A taxa de recebimento foi de ${periodCollectionRate.toLocaleString(
          'pt-BR',
          { maximumFractionDigits: 1 }
        )}%`,
        description: `${maskedMoney(periodPending)} permanecem pendentes.`,
        impact: 'Conversão de faturamento em caixa abaixo do ideal',
      });
    }

    if (receivedChange !== null && revenueChange !== null && receivedChange < revenueChange) {
      insights.push({
        level: 'warning',
        area: 'Caixa',
        title: 'Os recebimentos evoluíram abaixo do faturamento',
        description:
          'O saldo a receber exige acompanhamento quando o faturamento cresce mais rápido que o caixa.',
        impact: 'Possível aumento da necessidade de capital de giro',
      });
    }

    if (predictiveData.inactivePatients.length > 0) {
      insights.push({
        level: 'opportunity',
        area: 'Relacionamento',
        title: `${predictiveData.inactivePatients.length} paciente(s) sem retorno prioritário`,
        description:
          'Pacientes com maior tempo desde o último atendimento podem ser trabalhados em ações de recall.',
        impact: 'Potencial de recuperação de pacientes',
      });
      actions.push({
        label: 'Abrir pacientes sem retorno',
        action: () => navigateToPatients('inactive'),
      });
    }

    if (predictiveData.stalledTreatments.length > 0) {
      insights.push({
        level: 'opportunity',
        area: 'Tratamentos',
        title: `${predictiveData.stalledTreatments.length} tratamento(s) estão parados`,
        description:
          'Casos sem atualização por período prolongado podem indicar oportunidade de retomada.',
        impact: 'Produção clínica potencial não realizada',
      });
      actions.push({
        label: 'Revisar tratamentos parados',
        action: () => navigateToTreatments('stalled'),
      });
    }

    if (insights.length === 0) {
      insights.push({
        level: 'positive',
        area: 'Gestão',
        title: `Nenhuma tendência crítica foi identificada em ${periodDays} dias`,
        description:
          'Os dados atuais não indicam desvios relevantes.',
        impact: 'Operação estável no intervalo selecionado',
      });
    }

    const priorityRank = {
      critical: 0,
      warning: 1,
      opportunity: 2,
      positive: 3,
      neutral: 4,
    };

    const orderedInsights = [...insights]
      .sort((a, b) => (priorityRank[a.level] ?? 9) - (priorityRank[b.level] ?? 9))
      .slice(0, 6);

    const uniqueActions = actions.filter(
      (item, index, array) =>
        array.findIndex((candidate) => candidate.label === item.label) === index
    );

    const criticalCount = orderedInsights.filter((item) => item.level === 'critical').length;
    const warningCount = orderedInsights.filter((item) => item.level === 'warning').length;
    const opportunityCount = orderedInsights.filter((item) => item.level === 'opportunity').length;

    let headline = `A clínica apresenta estabilidade nos últimos ${periodDays} dias.`;
    let summary = 'Não foram encontrados desvios que exijam intervenção imediata.';

    if (criticalCount > 0) {
      headline = `Existem desvios relevantes nos últimos ${periodDays} dias.`;
      summary = 'Priorize os indicadores críticos antes das oportunidades de crescimento.';
    } else if (warningCount > 0) {
      headline = `Alguns indicadores dos últimos ${periodDays} dias merecem atenção.`;
      summary = 'Ajustes preventivos podem evitar impacto financeiro ou operacional.';
    } else if (opportunityCount > 0) {
      headline = `Há oportunidades de recuperação nos últimos ${periodDays} dias.`;
      summary = 'Pacientes, tratamentos e agenda podem gerar produção adicional.';
    }

    return {
      periodDays,
      insights: orderedInsights,
      actions: uniqueActions.slice(0, 4),
      headline,
      summary,
      criticalCount,
      warningCount,
      opportunityCount,
      hasPeriodData:
        Object.keys(periodFinancial).length > 0 || periodAppointments.length > 0,
    };
  }, [
    consultantPeriod,
    data.appointments,
    data.managementPeriods,
    predictiveData.inactivePatients.length,
    predictiveData.stalledTreatments.length,
  ]);

  const visiblePriorities = priorities.slice(0, 4);
  const visibleTodayAppointments = todayAppointments.slice(0, 6);

  if (loading) {
    return (
      <Layout>
        <DashboardSkeleton />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <section className="order-0 overflow-hidden rounded-2xl bg-gradient-to-r from-purple-700 to-indigo-700 text-white shadow-sm">
          <div className="p-6 sm:p-7">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <p className="text-sm capitalize text-purple-100">{formattedToday}</p>
                <h1 className="mt-1 text-2xl font-bold sm:text-3xl">
                  {greeting}, {firstName}.
                </h1>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-purple-100">
                  {dashboardPreferences.preset === 'essential'
                    ? 'Veja os atendimentos, lembretes e prioridades que exigem atenção hoje.'
                    : 'Acompanhe o que exige ação hoje, os resultados do mês e as oportunidades identificadas pela plataforma.'}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-purple-100">
                  <span>Atualizado em {generatedAtLabel}</span>
                  {data.partialErrors.length > 0 && (
                    <span className="rounded-full bg-amber-400/20 px-2 py-1 font-semibold text-amber-100">
                      Dados parciais
                    </span>
                  )}
                  {canViewFinancial &&
                    !financialValuesVisible &&
                    dashboardPreferences.preset !== 'essential' && (
                    <span className="rounded-full bg-white/15 px-2 py-1 font-semibold text-white">
                      Valores financeiros ocultos
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setShowMonthlySummary(true)}
                  className="inline-flex items-center rounded-lg border border-white/30 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/20"
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Resumo mensal
                </button>
                {canViewFinancial && dashboardPreferences.preset !== 'essential' && (
                  <button
                    type="button"
                    onClick={toggleFinancialValues}
                    className="inline-flex items-center rounded-lg border border-white/30 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/20"
                    title={
                      financialValuesVisible
                        ? 'Ocultar valores financeiros'
                        : 'Mostrar valores financeiros'
                    }
                  >
                    {financialValuesVisible ? (
                      <EyeOff className="mr-2 h-4 w-4" />
                    ) : (
                      <Eye className="mr-2 h-4 w-4" />
                    )}
                    {financialValuesVisible ? 'Ocultar valores' : 'Mostrar valores'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowDashboardSettings(true)}
                  className="inline-flex items-center rounded-lg border border-white/30 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/20"
                >
                  <SlidersHorizontal className="mr-2 h-4 w-4" />
                  Personalizar
                </button>
                <button
                  type="button"
                  onClick={() => loadDashboard({ silent: true })}
                  disabled={refreshing}
                  className="inline-flex items-center rounded-lg border border-white/30 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Atualizando...' : 'Atualizar'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/agenda')}
                  className="inline-flex items-center rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-purple-700 hover:bg-purple-50"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  Abrir agenda
                </button>
                {dashboardPreferences.preset !== 'essential' && (
                  <button
                    type="button"
                    onClick={openWhatsAppWeb}
                    className="inline-flex items-center rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    WhatsApp Web
                  </button>
                )}
              </div>
            </div>

            <div className={`${dashboardPreferences.blocks.heroMetrics ? '' : 'hidden'} mt-6 grid grid-cols-2 gap-3 lg:grid-cols-5`}>
              {[
                ['Agenda hoje', number(todayAppointments.length)],
                ['Confirmados', number(confirmedToday)],
                ['A confirmar', number(pendingConfirmations)],
                ['Previsto 30 dias', maskedMoney(cashForecast.next_30_days)],
                ['Recebido no mês', maskedMoney(finance.total_received || data.billing?.total_revenue)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-white/15 bg-white/10 p-3 backdrop-blur-sm">
                  <p className="text-xs text-purple-100">{label}</p>
                  <p className="mt-1 truncate font-bold">{value}</p>
                </div>
              ))}
            </div>

            <div className={`${dashboardPreferences.preset === 'essential' ? 'hidden' : ''} mt-5 border-t border-white/15 pt-4`}>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-purple-100">
                    Modo do Dashboard
                  </p>
                  <p className="mt-1 text-sm text-white">
                    {activeDashboardMode.label} · {activeDashboardMode.shortDescription}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {Object.entries(dashboardModes).map(([key, mode]) => {
                    const Icon = mode.icon;
                    const active = dashboardPreferences.mode === key;

                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => updateDashboardMode(key)}
                        className={`inline-flex items-center justify-center rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                          active
                            ? 'border-white bg-white text-purple-700'
                            : 'border-white/25 bg-white/10 text-white hover:bg-white/20'
                        }`}
                        title={mode.shortDescription}
                      >
                        <Icon className="mr-1.5 h-4 w-4" />
                        {mode.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        <ComplianceDashboardNudge />

        <section className={`${dashboardPreferences.preset === 'essential' ? 'hidden' : ''} order-2 rounded-xl border border-purple-200 bg-purple-50 p-4`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-purple-100 p-2 text-purple-700">
                {React.createElement(activeDashboardMode.icon, { className: 'h-5 w-5' })}
              </div>
              <div>
                <p className="font-semibold text-purple-900">
                  {adaptiveFocus.title}
                </p>
                <p className="mt-1 text-sm text-purple-700">
                  {adaptiveFocus.description}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowDashboardSettings(true)}
              className="text-sm font-semibold text-purple-700 hover:text-purple-900"
            >
              Ajustar comportamento
            </button>
          </div>
        </section>

        <section
          className={`${dashboardPreferences.sections.today ? '' : 'hidden'} ${sectionOrder.today} space-y-5`}
        >
          <div className="flex flex-col gap-2 border-b border-gray-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-purple-600">Hoje</p>
              <h2 className="mt-1 text-2xl font-bold text-gray-900">Operação do dia</h2>
              <p className="mt-1 text-sm text-gray-500">
                Agenda, prioridades e compromissos que podem exigir uma ação imediata.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/agenda')}
              className="inline-flex items-center text-sm font-semibold text-purple-600 hover:text-purple-700"
            >
              Ver agenda completa <ArrowRight className="ml-1 h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm xl:col-span-2">
              <div className="flex items-center justify-between gap-4 border-b border-gray-100 p-5">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Agenda de hoje</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Próximos atendimentos e situação de confirmação.
                  </p>
                </div>
                <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700">
                  {todayAppointments.length} atendimento(s)
                </span>
              </div>

              {visibleTodayAppointments.length === 0 ? (
                <div className="p-10 text-center text-gray-500">
                  <Calendar className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                  Nenhum atendimento registrado para hoje.
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {visibleTodayAppointments.map((appointment, index) => (
                    <button
                      type="button"
                      key={appointment.id || index}
                      onClick={() => navigate('/agenda')}
                      className="flex w-full items-center gap-4 p-4 text-left hover:bg-gray-50"
                    >
                      <div className="w-16 shrink-0 text-center">
                        <span className="font-bold text-gray-900">{appointmentTime(appointment)}</span>
                      </div>
                      <div className="w-px self-stretch bg-gray-200" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-gray-900">
                          {appointment.patient_name || appointment.patient?.full_name || appointment.title || 'Paciente'}
                        </p>
                        <p className="truncate text-sm text-gray-500">
                          {appointment.procedure || appointment.appointment_type || appointment.notes || 'Atendimento odontológico'}
                        </p>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(appointment)}`}>
                        {statusLabel(appointment)}
                      </span>
                    </button>
                  ))}
                  {todayAppointments.length > visibleTodayAppointments.length && (
                    <button
                      type="button"
                      onClick={() => navigate('/agenda')}
                      className="w-full px-4 py-3 text-center text-sm font-semibold text-purple-600 hover:bg-purple-50"
                    >
                      Ver mais {todayAppointments.length - visibleTodayAppointments.length} atendimento(s)
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Prioridades do dia</h3>
                  <p className="mt-1 text-sm text-gray-500">Ordenadas pelo impacto operacional.</p>
                </div>
                <AlertTriangle className="h-5 w-5 text-purple-600" />
              </div>

              <div className="mt-4 space-y-3">
                {visiblePriorities.map((item, index) => {
                  const style =
                    item.severity === 'critical'
                      ? 'border-red-200 bg-red-50 text-red-800'
                      : item.severity === 'warning'
                      ? 'border-amber-200 bg-amber-50 text-amber-800'
                      : item.severity === 'positive'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                      : 'border-blue-200 bg-blue-50 text-blue-800';
                  const Icon =
                    item.severity === 'positive'
                      ? CheckCircle2
                      : item.severity === 'info'
                      ? Bell
                      : AlertTriangle;

                  return (
                    <button
                      type="button"
                      key={`${item.title}-${index}`}
                      onClick={() => navigate(item.href)}
                      className={`w-full rounded-xl border p-3 text-left transition-all hover:shadow-sm ${style}`}
                    >
                      <div className="flex items-start gap-3">
                        <Icon className="mt-0.5 h-5 w-5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold">{item.title}</p>
                          <p className="mt-1 text-xs opacity-80">{item.detail}</p>
                        </div>
                        <ArrowRight className="mt-0.5 h-4 w-4 shrink-0" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 shadow-sm xl:col-span-2">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-amber-900">
                    <Bell className="h-5 w-5 text-amber-600" />
                    Lembretes de hoje
                  </h3>
                  <p className="mt-1 text-sm text-amber-800">
                    {data.reminders.length > 0
                      ? `${data.reminders.length} lembrete(s) programado(s).`
                      : 'Nenhum lembrete programado para hoje.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/lembretes')}
                  className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700"
                >
                  Ver lembretes
                </button>
              </div>

              {data.reminders.length > 0 && (
                <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
                  {data.reminders.slice(0, 4).map((reminder, index) => (
                    <button
                      type="button"
                      key={reminder.id || `${reminder.title || 'lembrete'}-${index}`}
                      onClick={() => navigate('/lembretes')}
                      className="rounded-lg border border-amber-100 bg-white px-3 py-3 text-left hover:border-amber-300"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {reminder.title || 'Lembrete'}
                        </p>
                        {isBirthdayReminder(reminder) && (
                          <span className="shrink-0 rounded-full bg-pink-100 px-2 py-0.5 text-[10px] font-semibold text-pink-700">
                            Aniversário
                          </span>
                        )}
                      </div>
                      <p className="mt-1 truncate text-xs text-gray-500">
                        {reminder.due_time ? `${reminder.due_time} • ` : ''}
                        {reminder.patient_name ? `Paciente: ${reminder.patient_name}` : 'Sem paciente vinculado'}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className={`${dashboardPreferences.blocks.quickLinks ? '' : 'hidden'} rounded-xl border border-gray-200 bg-white p-5 shadow-sm`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Acessos rápidos</h3>
                  <p className="mt-1 text-sm text-gray-500">Rotinas mais frequentes.</p>
                </div>
                <ArrowRight className="h-5 w-5 text-purple-600" />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {quickLinks.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      type="button"
                      key={item.label}
                      onClick={() => navigate(item.href)}
                      className="rounded-lg border border-gray-200 p-3 text-left transition-colors hover:border-purple-300 hover:bg-purple-50"
                    >
                      <Icon className="h-5 w-5 text-purple-600" />
                      <p className="mt-2 text-sm font-medium text-gray-900">{item.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section
          className={`${dashboardPreferences.blocks.dailyGoals ? '' : 'hidden'} order-3 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm`}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <div className="rounded-lg bg-emerald-100 p-2 text-emerald-700">
                  <ListChecks className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-600">
                    Meta de hoje
                  </p>
                  <h2 className="mt-1 text-xl font-bold text-gray-900">
                    {completedDailyGoalsCount} de {dailyGoals.length} metas concluídas
                  </h2>
                </div>
              </div>
              <p className="mt-3 text-sm text-gray-600">
                Prioridades sugeridas com base na situação atual da clínica.
              </p>
            </div>

            <div className="min-w-[220px] rounded-xl border border-emerald-100 bg-emerald-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-emerald-900">Progresso</span>
                <span className="text-sm font-bold text-emerald-700">{dailyGoalsProgress}%</span>
              </div>
              <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-emerald-100">
                <div
                  className="h-full rounded-full bg-emerald-600 transition-all"
                  style={{ width: `${dailyGoalsProgress}%` }}
                />
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
            {dailyGoals.map((goal) => {
              const Icon = goal.icon;
              const completed = completedDailyGoals.includes(goal.id);

              return (
                <div
                  key={goal.id}
                  className={`rounded-xl border p-4 transition ${
                    completed
                      ? 'border-emerald-200 bg-emerald-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => toggleDailyGoal(goal.id)}
                      className={`mt-0.5 shrink-0 rounded-full ${
                        completed
                          ? 'text-emerald-600'
                          : 'text-gray-300 hover:text-emerald-500'
                      }`}
                      title={completed ? 'Marcar como pendente' : 'Marcar como concluída'}
                    >
                      {completed ? (
                        <CheckCircle2 className="h-6 w-6" />
                      ) : (
                        <Circle className="h-6 w-6" />
                      )}
                    </button>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p
                            className={`font-semibold ${
                              completed
                                ? 'text-emerald-900 line-through'
                                : 'text-gray-900'
                            }`}
                          >
                            {goal.title}
                          </p>
                          <p className="mt-1 text-sm text-gray-600">{goal.description}</p>
                        </div>
                        <div
                          className={`rounded-lg p-2 ${
                            completed
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-purple-50 text-purple-600'
                          }`}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => navigate(goal.href)}
                        className="mt-3 inline-flex items-center text-sm font-semibold text-purple-600 hover:text-purple-700"
                      >
                        Abrir área relacionada
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {dailyGoalsProgress === 100 && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800">
              Todas as metas do dia foram concluídas.
            </div>
          )}
        </section>

        <section
          className={`${dashboardPreferences.blocks.executiveAssistant ? '' : 'hidden'} order-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm`}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2">
                <div className="rounded-lg bg-indigo-100 p-2 text-indigo-700">
                  <Lightbulb className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">
                    Assistente executivo
                  </p>
                  <h2 className="mt-1 text-xl font-bold text-gray-900">
                    {executiveAssistant.headline}
                  </h2>
                </div>
              </div>
              <p className="mt-3 text-sm leading-6 text-gray-600">
                {executiveAssistant.summary}
              </p>
            </div>

            <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-800">
              Atualizado com os dados atuais do Dashboard.
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-gray-500" />
                <h3 className="font-semibold text-gray-900">Pontos de atenção</h3>
              </div>

              <div className="mt-3 space-y-3">
                {executiveAssistant.alerts.map((alert, index) => (
                  <div
                    key={`${alert.title}-${index}`}
                    className={`rounded-xl border p-4 ${
                      alert.level === 'critical'
                        ? 'border-red-200 bg-red-50'
                        : alert.level === 'warning'
                        ? 'border-amber-200 bg-amber-50'
                        : alert.level === 'opportunity'
                        ? 'border-blue-200 bg-blue-50'
                        : 'border-green-200 bg-green-50'
                    }`}
                  >
                    <p className="font-semibold text-gray-900">{alert.title}</p>
                    <p className="mt-1 text-sm text-gray-600">{alert.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-gray-500" />
                <h3 className="font-semibold text-gray-900">Ações sugeridas</h3>
              </div>

              <div className="mt-3 space-y-3">
                {executiveAssistant.recommendations.map((item, index) => (
                  <button
                    key={`${item.label}-${index}`}
                    type="button"
                    onClick={item.action}
                    className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white p-4 text-left transition hover:border-indigo-300 hover:bg-indigo-50"
                  >
                    <span className="font-medium text-gray-900">{item.label}</span>
                    <ArrowRight className="h-4 w-4 text-indigo-600" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section
          className={`${dashboardPreferences.blocks.managementConsultant ? '' : 'hidden'} order-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm`}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-purple-100 p-2 text-purple-700">
                  <TrendingUp className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-purple-600">
                    Consultor de gestão
                  </p>
                  <h2 className="mt-1 text-xl font-bold text-gray-900">
                    {managementConsultant.headline}
                  </h2>
                </div>
              </div>
              <p className="mt-3 text-sm leading-6 text-gray-600">
                {managementConsultant.summary}
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Período
              </span>
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                {[
                  ['30', '30 dias'],
                  ['90', '90 dias'],
                  ['180', '180 dias'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setConsultantPeriod(value)}
                    className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                      consultantPeriod === value
                        ? 'bg-white text-purple-700 shadow-sm'
                        : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-red-100 bg-red-50 p-3 text-center">
              <p className="text-2xl font-bold text-red-700">
                {managementConsultant.criticalCount}
              </p>
              <p className="mt-1 text-xs font-medium text-red-700">Críticos</p>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-3 text-center">
              <p className="text-2xl font-bold text-amber-700">
                {managementConsultant.warningCount}
              </p>
              <p className="mt-1 text-xs font-medium text-amber-700">Atenções</p>
            </div>
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-center">
              <p className="text-2xl font-bold text-blue-700">
                {managementConsultant.opportunityCount}
              </p>
              <p className="mt-1 text-xs font-medium text-blue-700">Oportunidades</p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
            <div className="space-y-3 xl:col-span-2">
              {managementConsultant.insights.map((insight, index) => {
                const styles =
                  insight.level === 'critical'
                    ? 'border-red-200 bg-red-50'
                    : insight.level === 'warning'
                    ? 'border-amber-200 bg-amber-50'
                    : insight.level === 'opportunity'
                    ? 'border-blue-200 bg-blue-50'
                    : insight.level === 'positive'
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-gray-200 bg-gray-50';

                const badge =
                  insight.level === 'critical'
                    ? 'bg-red-100 text-red-700'
                    : insight.level === 'warning'
                    ? 'bg-amber-100 text-amber-700'
                    : insight.level === 'opportunity'
                    ? 'bg-blue-100 text-blue-700'
                    : insight.level === 'positive'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-gray-100 text-gray-700';

                return (
                  <div
                    key={`${insight.area}-${index}`}
                    className={`rounded-xl border p-4 ${styles}`}
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${badge}`}
                        >
                          {insight.area}
                        </span>
                        <h3 className="mt-3 font-semibold text-gray-900">
                          {insight.title}
                        </h3>
                        <p className="mt-1 text-sm leading-6 text-gray-600">
                          {insight.description}
                        </p>
                      </div>
                      <div className="shrink-0 rounded-lg border border-white/70 bg-white/70 px-3 py-2 sm:max-w-[210px]">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                          Impacto
                        </p>
                        <p className="mt-1 text-xs font-medium text-gray-800">
                          {insight.impact}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-purple-600" />
                <h3 className="font-semibold text-gray-900">
                  Próximas ações
                </h3>
              </div>

              <div className="mt-4 space-y-3">
                {managementConsultant.actions.length > 0 ? (
                  managementConsultant.actions.map((item, index) => (
                    <button
                      key={`${item.label}-${index}`}
                      type="button"
                      onClick={item.action}
                      className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white p-3 text-left text-sm font-semibold text-gray-900 transition hover:border-purple-300 hover:bg-purple-50"
                    >
                      {item.label}
                      <ArrowRight className="ml-3 h-4 w-4 shrink-0 text-purple-600" />
                    </button>
                  ))
                ) : (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                    Continue acompanhando os indicadores para preservar a estabilidade.
                  </div>
                )}
              </div>

              {!managementConsultant.hasPeriodData && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  Não há dados suficientes para o período selecionado.
                </div>
              )}

              <p className="mt-4 text-xs leading-5 text-gray-500">
                Análise determinística calculada para os últimos{' '}
                {managementConsultant.periodDays} dias e comparada, quando possível,
                com o intervalo anterior de mesma duração.
              </p>
            </div>
          </div>
        </section>

        <section
          className={`${dashboardPreferences.sections.management ? '' : 'hidden'} ${sectionOrder.management} space-y-5`}
        >
          {!canViewFinancial && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
              Os valores financeiros estão ocultos para este perfil. Os indicadores operacionais permanecem disponíveis.
            </div>
          )}

          <div className="flex flex-col gap-2 border-b border-gray-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-purple-600">Gestão do mês</p>
              <h2 className="mt-1 text-2xl font-bold text-gray-900">Resultado e saúde da clínica</h2>
              <p className="mt-1 text-sm text-gray-500">
                Indicadores consolidados para acompanhar desempenho, caixa e operação.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigateToFinancial('reports')}
              className="inline-flex items-center text-sm font-semibold text-purple-600 hover:text-purple-700"
            >
              Abrir relatórios <ArrowRight className="ml-1 h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <ExecutiveCard
              title="Recebido no mês"
              value={maskedMoney(finance.total_received || data.billing?.total_revenue)}
              description="Receitas confirmadas"
              icon={Wallet}
              delta={comparison?.available ? comparison.received_change_percentage : undefined}
              onClick={() => navigateToFinancial('payments')}
            />
            <ExecutiveCard
              title="Faturado no mês"
              value={maskedMoney(finance.total_invoiced)}
              description="Faturas e receitas registradas"
              icon={Receipt}
              delta={comparison?.available ? comparison.invoiced_change_percentage : undefined}
              onClick={() => navigateToFinancial('invoices')}
            />
            <ExecutiveCard
              title="Saldo a receber"
              value={maskedMoney(finance.total_pending)}
              description={`${collectionRate.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% recebido`}
              icon={DollarSign}
              onClick={() => navigateToFinancial('invoices')}
            />
            <ExecutiveCard
              title="Novos pacientes"
              value={number(newPatientsThisMonth)}
              description={`${number(data.patients.length)} pacientes cadastrados`}
              icon={Users}
              onClick={() => navigateToPatients()}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm xl:col-span-2">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Evolução financeira</h3>
                  <p className="mt-1 text-sm text-gray-500">Faturado e recebido nos últimos meses.</p>
                </div>
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div className="mt-4 h-72">
                {monthlyRevenueHistory.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthlyRevenueHistory}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis
                        tickFormatter={(value) => `R$ ${Number(value || 0).toLocaleString('pt-BR')}`}
                        width={85}
                      />
                      <Tooltip formatter={(value) => maskedMoney(value)} />
                      <Line type="monotone" dataKey="invoiced" name="Faturado" stroke="#7c3aed" strokeWidth={2} />
                      <Line type="monotone" dataKey="received" name="Recebido" stroke="#10b981" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full items-center justify-center text-center text-gray-500">
                    Ainda não há histórico financeiro suficiente para o gráfico.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Saúde da clínica</h3>
                  <p className="mt-1 text-sm text-gray-500">Índice consolidado de 0 a 100.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowClinicScoreDetails(true)}
                  className="inline-flex items-center rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-100"
                >
                  <Info className="mr-2 h-4 w-4" />
                  Entender score
                </button>
              </div>

              <div className="mt-5 flex items-center gap-5">
                <button
                  type="button"
                  onClick={() => setShowClinicScoreDetails(true)}
                  className="flex h-28 w-28 shrink-0 items-center justify-center rounded-full border-[10px] border-purple-100 transition hover:border-purple-200"
                  title="Ver composição do score"
                >
                  <div className="text-center">
                    <p className="text-3xl font-bold text-purple-700">
                      {clinicScore === null ? '—' : clinicScore}
                    </p>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">
                      {clinicScore === null ? 'sem base' : 'de 100'}
                    </p>
                  </div>
                </button>
                <div>
                  <p className="text-xl font-bold text-gray-900">{clinicScoreLabel}</p>
                  <p className="mt-1 text-sm text-gray-500">
                    Agenda, financeiro, pacientes e operação.
                  </p>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                {[
                  ['Agenda', agendaScore],
                  ['Financeiro', financeScore],
                  ['Pacientes', patientScore],
                  ['Operacional', operationalScore],
                ].map(([label, score]) => (
                  <div key={label} className="rounded-lg bg-gray-50 p-3">
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className="mt-1 text-lg font-bold text-gray-900">
                      {score === null || score === undefined ? '—' : Math.round(score)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <HealthCard
              title="Agenda"
              status={agendaHealth}
              description={`${confirmedToday} confirmado(s), ${pendingConfirmations} pendente(s)`}
              icon={Calendar}
              onClick={() => navigate('/agenda')}
            />
            <HealthCard
              title="Financeiro"
              status={financeHealth}
              description={
                !hasFinancialBase
                  ? 'Sem movimentação financeira no período'
                  : overdue > 0
                  ? `${maskedMoney(overdue)} vencidos`
                  : `${maskedMoney(pending)} a receber`
              }
              icon={TrendingUp}
              onClick={() => navigateToFinancial('reports')}
            />
            <HealthCard
              title="Pacientes"
              status={patientHealth}
              description={
                data.patients.length === 0
                  ? 'Nenhum paciente cadastrado'
                  : `${newPatientsThisMonth} novo(s) paciente(s) no mês`
              }
              icon={Users}
              onClick={() => navigateToPatients('inactive')}
            />
            <HealthCard
              title="Estoque"
              status={stockHealth}
              description={lowStock > 0 ? `${lowStock} item(ns) em nível baixo` : 'Níveis dentro do esperado'}
              icon={Package}
              onClick={() => navigate('/estoque')}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Financeiro próximo</h3>
                  <p className="mt-1 text-sm text-gray-500">Valores que exigem acompanhamento.</p>
                </div>
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="mt-5 space-y-3">
                {[
                  ['Vencidos e hoje', cashForecast.overdue_and_today],
                  ['Próximos 7 dias', cashForecast.next_7_days],
                  ['Próximos 30 dias', cashForecast.next_30_days],
                  ['Saldo total pendente', finance.total_pending],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-4 border-b border-gray-100 py-2 last:border-0">
                    <span className="text-sm text-gray-600">{label}</span>
                    <span className="text-sm font-semibold text-gray-900">{maskedMoney(value)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Alinhadores</h3>
                  <p className="mt-1 text-sm text-gray-500">Visão consolidada dos casos.</p>
                </div>
                <Smile className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {[
                  ['Casos', aligners.total_cases],
                  ['Em andamento', aligners.in_progress],
                  ['Concluídos', aligners.completed],
                  ['Receita vinculada', maskedMoney(aligners.revenue)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg bg-gray-50 p-3">
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className="mt-1 truncate font-bold text-gray-900">
                      {typeof value === 'string' ? value : number(value)}
                    </p>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => navigate('/alinhadores')}
                className="mt-4 inline-flex w-full items-center justify-center rounded-lg border border-indigo-200 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
              >
                Abrir módulo de alinhadores
              </button>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Panorama operacional</h3>
                <p className="mt-1 text-sm text-gray-500">Indicadores complementares.</p>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {[
                  { label: 'Pacientes', value: data.patients.length, icon: Users },
                  { label: 'Tratamentos ativos', value: activeTreatments, icon: FileText },
                  { label: 'Agendamentos hoje', value: todayAppointments.length, icon: Clock },
                  { label: 'Parceiros com receita', value: finance.partner_breakdown?.length || 0, icon: Building2 },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="rounded-lg bg-gray-50 p-3">
                      <Icon className="h-4 w-4 text-gray-500" />
                      <p className="mt-2 text-xl font-bold text-gray-900">{number(item.value)}</p>
                      <p className="truncate text-xs text-gray-500">{item.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section
          className={`${dashboardPreferences.sections.intelligence ? '' : 'hidden'} ${sectionOrder.intelligence} space-y-5`}
        >
          <div className="border-b border-gray-200 pb-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-purple-600">Inteligência e previsão</p>
            <h2 className="mt-1 text-2xl font-bold text-gray-900">Sinais para decisões futuras</h2>
            <p className="mt-1 text-sm text-gray-500">
              Estimativas baseadas no histórico disponível. Use-as como apoio operacional, não como certeza.
            </p>
          </div>

          <div className={`${dashboardPreferences.blocks.recommendedActions ? '' : 'hidden'} rounded-xl border border-gray-200 bg-white p-5 shadow-sm`}>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Ações recomendadas</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Atalhos diretos para situações que merecem acompanhamento.
                </p>
              </div>
              <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700">
                Acesso rápido
              </span>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
              <button
                type="button"
                onClick={() => navigateToFinancial('invoices', { status: 'vencido' })}
                className={`rounded-xl border p-4 text-left transition-all hover:shadow-sm ${
                  overdue > 0
                    ? 'border-red-200 bg-red-50 hover:border-red-300'
                    : 'border-gray-200 bg-gray-50 hover:border-purple-300'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className={`rounded-lg p-2 ${
                    overdue > 0 ? 'bg-red-100 text-red-700' : 'bg-white text-gray-500'
                  }`}>
                    <Receipt className="h-5 w-5" />
                  </div>
                  <ArrowRight className={`h-4 w-4 ${
                    overdue > 0 ? 'text-red-600' : 'text-gray-400'
                  }`} />
                </div>
                <p className="mt-3 font-semibold text-gray-900">Cobranças vencidas</p>
                <p className={`mt-1 text-sm ${
                  overdue > 0 ? 'text-red-700' : 'text-gray-500'
                }`}>
                  {overdue > 0
                    ? `${maskedMoney(overdue)} aguardando regularização`
                    : 'Nenhuma cobrança vencida no momento'}
                </p>
              </button>

              <button
                type="button"
                onClick={() => navigateToPatients('inactive')}
                className={`rounded-xl border p-4 text-left transition-all hover:shadow-sm ${
                  predictiveData.inactivePatients.length > 0
                    ? 'border-amber-200 bg-amber-50 hover:border-amber-300'
                    : 'border-gray-200 bg-gray-50 hover:border-purple-300'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className={`rounded-lg p-2 ${
                    predictiveData.inactivePatients.length > 0
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-white text-gray-500'
                  }`}>
                    <Users className="h-5 w-5" />
                  </div>
                  <ArrowRight className={`h-4 w-4 ${
                    predictiveData.inactivePatients.length > 0
                      ? 'text-amber-600'
                      : 'text-gray-400'
                  }`} />
                </div>
                <p className="mt-3 font-semibold text-gray-900">Pacientes sem retorno</p>
                <p className={`mt-1 text-sm ${
                  predictiveData.inactivePatients.length > 0
                    ? 'text-amber-700'
                    : 'text-gray-500'
                }`}>
                  {predictiveData.inactivePatients.length > 0
                    ? `${predictiveData.inactivePatients.length} paciente(s) identificado(s)`
                    : 'Nenhum paciente identificado nessa condição'}
                </p>
              </button>

              <button
                type="button"
                onClick={() => navigateToTreatments('stalled')}
                className={`rounded-xl border p-4 text-left transition-all hover:shadow-sm ${
                  predictiveData.stalledTreatments.length > 0
                    ? 'border-blue-200 bg-blue-50 hover:border-blue-300'
                    : 'border-gray-200 bg-gray-50 hover:border-purple-300'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className={`rounded-lg p-2 ${
                    predictiveData.stalledTreatments.length > 0
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-white text-gray-500'
                  }`}>
                    <FileText className="h-5 w-5" />
                  </div>
                  <ArrowRight className={`h-4 w-4 ${
                    predictiveData.stalledTreatments.length > 0
                      ? 'text-blue-600'
                      : 'text-gray-400'
                  }`} />
                </div>
                <p className="mt-3 font-semibold text-gray-900">Tratamentos parados</p>
                <p className={`mt-1 text-sm ${
                  predictiveData.stalledTreatments.length > 0
                    ? 'text-blue-700'
                    : 'text-gray-500'
                }`}>
                  {predictiveData.stalledTreatments.length > 0
                    ? `${predictiveData.stalledTreatments.length} tratamento(s) identificado(s)`
                    : 'Nenhum tratamento identificado nessa condição'}
                </p>
              </button>
            </div>
          </div>

          <div className={`${dashboardPreferences.blocks.predictiveCards ? '' : 'hidden'} grid grid-cols-1 gap-4 xl:grid-cols-4`}>
            <div className="rounded-xl border border-red-100 bg-red-50 p-4">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-red-900">Risco de falta hoje</p>
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              {predictiveData.noShowRisks.length === 0 ? (
                <p className="mt-3 text-sm text-red-700">Nenhum risco relevante identificado.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {predictiveData.noShowRisks.slice(0, 3).map((item, index) => (
                    <button
                      key={item.id || index}
                      type="button"
                      onClick={() => navigate('/agenda')}
                      className="w-full rounded-lg border border-red-100 bg-white p-3 text-left"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-gray-900">
                          {item.time} • {item.name}
                        </span>
                        <span className="text-xs font-bold text-red-700">{item.score}%</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-amber-100 bg-amber-50 p-4">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-amber-900">Pacientes sem retorno</p>
                <Users className="h-5 w-5 text-amber-600" />
              </div>
              <p className="mt-3 text-3xl font-bold text-amber-900">{predictiveData.inactivePatients.length}</p>
              <p className="mt-1 text-xs text-amber-700">Última consulta há 180 dias ou mais.</p>
              {predictiveData.inactivePatients.slice(0, 3).map((item, index) => (
                <button
                  key={item.id || index}
                  type="button"
                  onClick={() => navigateToPatients('inactive')}
                  className="mt-2 block w-full truncate text-left text-xs text-amber-900"
                >
                  {item.name} • {item.days} dias
                </button>
              ))}
            </div>

            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-blue-900">Tratamentos parados</p>
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <p className="mt-3 text-3xl font-bold text-blue-900">{predictiveData.stalledTreatments.length}</p>
              <p className="mt-1 text-xs text-blue-700">Ativos sem atualização há 90 dias ou mais.</p>
              {predictiveData.stalledTreatments.slice(0, 3).map((item, index) => (
                <button
                  key={item.id || index}
                  type="button"
                  onClick={() => navigateToTreatments('stalled')}
                  className="mt-2 block w-full truncate text-left text-xs text-blue-900"
                >
                  {item.patient} • {item.procedure} • {item.days} dias
                </button>
              ))}
            </div>

            <div className="rounded-xl border border-purple-100 bg-purple-50 p-4">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-purple-900">Previsão e ociosidade</p>
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              {predictiveData.projectionRange ? (
                <>
                  <p className="mt-3 text-sm text-purple-700">Faixa provável de fechamento</p>
                  <p className="mt-1 font-bold text-purple-900">
                    {maskedMoney(predictiveData.projectionRange.low)} a {maskedMoney(predictiveData.projectionRange.high)}
                  </p>
                </>
              ) : (
                <p className="mt-3 text-sm text-purple-700">Sem base financeira suficiente para projeção.</p>
              )}
              <div className="mt-4 border-t border-purple-100 pt-3">
                <p className="text-xs text-purple-700">Dia historicamente mais fraco</p>
                <p className="mt-1 font-semibold capitalize text-purple-900">
                  {predictiveData.weakestDay.occupancy === null
                    ? 'Sem histórico suficiente'
                    : `${predictiveData.weakestDay.label} • ${predictiveData.weakestDay.occupancy}% da referência`}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className={`${dashboardPreferences.blocks.performance ? '' : 'hidden'} rounded-xl border border-gray-200 bg-white p-5 shadow-sm`}>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Indicadores de desempenho</h3>
                <p className="mt-1 text-sm text-gray-500">Percentuais calculados com os dados disponíveis.</p>
              </div>
              <div className="mt-5 space-y-5">
                <ProgressMetric
                  label="Confirmação da agenda de hoje"
                  value={confirmationRate}
                  unavailable={!hasTodayAgendaBase}
                  detail={
                    hasTodayAgendaBase
                      ? `${confirmedToday} de ${todayAppointments.length} agendamento(s) confirmados`
                      : 'Nenhum agendamento registrado hoje'
                  }
                />
                <ProgressMetric
                  label="Execução da agenda de hoje"
                  value={executionRate}
                  unavailable={!hasTodayAgendaBase}
                  detail={
                    hasTodayAgendaBase
                      ? `${completedToday} concluído(s) e ${inProgressToday} em atendimento`
                      : 'Nenhum agendamento registrado hoje'
                  }
                />
                <ProgressMetric
                  label="Taxa de recebimento do mês"
                  value={collectionRate}
                  unavailable={!hasFinancialBase}
                  detail={
                    hasFinancialBase
                      ? `${maskedMoney(finance.total_received)} recebidos sobre o faturamento do período`
                      : 'Ainda não há movimentação financeira no período'
                  }
                />
                <ProgressMetric
                  label="Saúde operacional"
                  value={operationalScore}
                  detail={lowStock > 0 ? `${lowStock} item(ns) com estoque baixo` : 'Sem alertas de estoque baixo'}
                />
              </div>
            </div>

            <div className={`${dashboardPreferences.blocks.dailyReading ? '' : 'hidden'} rounded-xl border border-gray-200 bg-white p-5 shadow-sm`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Leitura automática do dia</h3>
                  <p className="mt-1 text-sm text-gray-500">Resumo determinístico dos indicadores atuais.</p>
                </div>
                <Activity className="h-6 w-6 text-purple-600" />
              </div>
              <div className="mt-5 space-y-3">
                {dailyInsights.map((insight, index) => (
                  <div key={index} className="rounded-lg border border-purple-100 bg-purple-50 p-3">
                    <p className="text-sm leading-relaxed text-purple-900">{insight}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-lg border border-emerald-100 bg-emerald-50 p-3">
                <p className="text-sm text-emerald-900">
                  {Number(cashProjection.projected_month_revenue || 0) > Number(finance.total_received || 0)
                    ? `Mantido o ritmo atual, o mês pode encerrar em aproximadamente ${maskedMoney(cashProjection.projected_month_revenue)}.`
                    : 'A projeção ficará mais precisa conforme novos recebimentos e vencimentos forem registrados.'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {showClinicScoreDetails && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowClinicScoreDetails(false)}
          >
            <div
              className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-purple-600">
                    Score geral da clínica
                  </p>
                  <h2 className="mt-1 text-2xl font-bold text-gray-900">
                    {clinicScore === null ? 'Sem dados suficientes' : `${clinicScore} de 100`}
                  </h2>
                  <p className="mt-2 text-sm text-gray-600">
                    O índice combina agenda, financeiro, pacientes e operação.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowClinicScoreDetails(false)}
                  className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
                  aria-label="Fechar detalhamento do score"
                >
                  <Settings className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-6 p-5">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {clinicScoreBreakdown.map((item) => {
                    const Icon = item.icon;
                    const unavailable =
                      item.score === null || item.score === undefined;

                    return (
                      <div
                        key={item.key}
                        className="rounded-xl border border-gray-200 bg-white p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="rounded-lg bg-purple-50 p-2 text-purple-600">
                              <Icon className="h-5 w-5" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{item.label}</p>
                              <p className="text-xs text-gray-500">
                                Peso original: {item.weight}%
                              </p>
                            </div>
                          </div>
                          <span
                            className={`rounded-full px-2.5 py-1 text-sm font-bold ${
                              unavailable
                                ? 'bg-gray-100 text-gray-500'
                                : Number(item.score) >= 85
                                ? 'bg-green-100 text-green-700'
                                : Number(item.score) >= 70
                                ? 'bg-blue-100 text-blue-700'
                                : Number(item.score) >= 50
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {unavailable ? 'Sem base' : Math.round(item.score)}
                          </span>
                        </div>

                        <p className="mt-3 text-sm leading-6 text-gray-600">
                          {item.description}
                        </p>

                        {!unavailable && (
                          <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
                            <div
                              className="h-full rounded-full bg-purple-600"
                              style={{ width: `${Math.max(0, Math.min(100, Number(item.score)))}%` }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                  <h3 className="font-semibold text-gray-900">
                    Por que o score não chegou a 100?
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Estimativa dos pontos perdidos por dimensão.
                  </p>

                  <div className="mt-4 space-y-3">
                    {clinicScoreLosses.filter((item) => item.loss > 0).length > 0 ? (
                      clinicScoreLosses
                        .filter((item) => item.loss > 0)
                        .map((item) => (
                          <div
                            key={item.key}
                            className="flex items-center justify-between gap-4 rounded-lg bg-white p-3"
                          >
                            <div>
                              <p className="font-medium text-gray-900">{item.label}</p>
                              <p className="mt-1 text-xs text-gray-500">{item.description}</p>
                            </div>
                            <span className="shrink-0 rounded-full bg-red-50 px-3 py-1 text-sm font-bold text-red-700">
                              −{item.loss} ponto(s)
                            </span>
                          </div>
                        ))
                    ) : (
                      <div className="rounded-lg bg-green-50 p-4 text-sm text-green-700">
                        Nenhuma perda relevante foi identificada nas dimensões disponíveis.
                      </div>
                    )}
                  </div>
                </div>

                {clinicScoreAvailableWeight < 100 && (
                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                    O score foi recalculado proporcionalmente porque algumas dimensões não
                    possuem dados suficientes neste momento.
                  </div>
                )}
              </div>

              <div className="flex justify-end border-t border-gray-200 p-5">
                <button
                  type="button"
                  onClick={() => setShowClinicScoreDetails(false)}
                  className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {showDashboardSettings && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowDashboardSettings(false)}
          >
            <div
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-gray-200 p-5">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Personalizar Dashboard</h2>
                  <p className="mt-1 text-sm text-gray-500">
                    A configuração é salva para este usuário nesta clínica.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDashboardSettings(false)}
                  className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
                  aria-label="Fechar personalização"
                >
                  <Settings className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-6 p-5">
                <div>
                  <h3 className="font-semibold text-gray-900">Nível de detalhes</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Escolha uma base pronta. Depois, você pode ajustar qualquer item individualmente.
                  </p>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {[
                      {
                        key: 'essential',
                        label: 'Essencial',
                        description: 'Agenda, prioridades, lembretes e acessos rápidos para começar o dia.',
                      },
                      {
                        key: 'complete',
                        label: 'Gestão completa',
                        description: 'Exibe também indicadores, metas, gestão, desempenho e previsões.',
                      },
                    ].map((preset) => {
                      const active = dashboardPreferences.preset === preset.key;
                      return (
                        <button
                          key={preset.key}
                          type="button"
                          onClick={() => applyDashboardPreset(preset.key)}
                          className={`rounded-xl border p-4 text-left transition ${
                            active
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-purple-200'
                          }`}
                        >
                          <div className="font-semibold text-gray-900">{preset.label}</div>
                          <p className="mt-2 text-sm text-gray-600">{preset.description}</p>
                        </button>
                      );
                    })}
                  </div>
                  {dashboardPreferences.preset === 'custom' && (
                    <p className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
                      Personalizado: há ajustes individuais diferentes dos padrões prontos.
                    </p>
                  )}
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900">Comportamento</h3>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {[
                      {
                        key: 'intelligent',
                        label: 'Inteligente',
                        icon: Sparkles,
                        description:
                          'Mantém a operação do dia no topo e escolhe automaticamente qual área deve receber maior destaque.',
                      },
                      {
                        key: 'operational',
                        label: 'Operacional',
                        icon: BriefcaseBusiness,
                        description:
                          'Prioriza agenda, confirmações, lembretes e acompanhamento da rotina clínica.',
                      },
                      {
                        key: 'financial',
                        label: 'Financeiro',
                        icon: DollarSign,
                        description:
                          'Coloca resultado do mês, recebimentos, inadimplência e projeções antes das análises de crescimento.',
                      },
                      {
                        key: 'growth',
                        label: 'Crescimento',
                        icon: UserRoundPlus,
                        description:
                          'Prioriza pacientes sem retorno, tratamentos parados, previsões e oportunidades de produção.',
                      },
                    ].map((mode) => {
                      const Icon = mode.icon;
                      const active = dashboardPreferences.mode === mode.key;

                      return (
                        <button
                          key={mode.key}
                          type="button"
                          onClick={() => updateDashboardMode(mode.key)}
                          className={`rounded-xl border p-4 text-left transition ${
                            active
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-purple-200'
                          }`}
                        >
                          <div className="flex items-center gap-2 font-semibold text-gray-900">
                            <Icon className="h-5 w-5 text-purple-600" />
                            {mode.label}
                          </div>
                          <p className="mt-2 text-sm text-gray-600">
                            {mode.description}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900">Seções principais</h3>
                  <div className="mt-3 space-y-2">
                    {[
                      ['today', 'Operação do dia'],
                      ['management', 'Gestão do mês'],
                      ['intelligence', 'Inteligência e previsão'],
                    ].map(([key, label]) => (
                      <label key={key} className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 p-3">
                        <span className="text-sm font-medium text-gray-800">{label}</span>
                        <input
                          type="checkbox"
                          checked={dashboardPreferences.sections[key]}
                          onChange={() => toggleDashboardSection(key)}
                          className="h-4 w-4 rounded border-gray-300 text-purple-600"
                        />
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900">Blocos opcionais</h3>
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {[
                      ['heroMetrics', 'Indicadores do cabeçalho'],
                      ['quickLinks', 'Atalhos rápidos'],
                      ['recommendedActions', 'Ações recomendadas'],
                      ['predictiveCards', 'Cards preditivos'],
                      ['performance', 'Indicadores de desempenho'],
                      ['dailyReading', 'Leitura automática do dia'],
                      ['executiveAssistant', 'Assistente executivo'],
                      ['dailyGoals', 'Metas do dia'],
                      ['managementConsultant', 'Consultor de gestão'],
                    ].map(([key, label]) => (
                      <label key={key} className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 p-3">
                        <span className="text-sm font-medium text-gray-800">{label}</span>
                        <input
                          type="checkbox"
                          checked={dashboardPreferences.blocks[key]}
                          onChange={() => toggleDashboardBlock(key)}
                          className="h-4 w-4 rounded border-gray-300 text-purple-600"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse gap-2 border-t border-gray-200 p-5 sm:flex-row sm:justify-between">
                <button
                  type="button"
                  onClick={restoreDashboardDefaults}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Restaurar gestão completa
                </button>
                <button
                  type="button"
                  onClick={() => setShowDashboardSettings(false)}
                  className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700"
                >
                  Concluir
                </button>
              </div>
            </div>
          </div>
        )}

        {showMonthlySummary && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 sm:p-4"
            onClick={() => setShowMonthlySummary(false)}
          >
            <div
              className="max-h-[95vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-gray-200 p-5">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Resumo mensal</h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Consulte os resultados de um mês e crie uma imagem pronta para compartilhar.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowMonthlySummary(false)}
                  className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
                  aria-label="Fechar resumo mensal"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid gap-6 p-5 lg:grid-cols-[minmax(0,1fr)_280px]">
                <div>
                  <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <label htmlFor="monthly-summary-period" className="text-sm font-semibold text-gray-700">
                      Mês do resumo
                    </label>
                    <select
                      id="monthly-summary-period"
                      value={selectedSummaryMonth}
                      onChange={(event) => setSelectedSummaryMonth(event.target.value)}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm capitalize text-gray-800 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
                    >
                      {monthOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label.charAt(0).toUpperCase() + option.label.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-purple-700 via-indigo-600 to-indigo-900 p-6 text-white shadow-lg sm:p-8">
                    <div className="flex items-center gap-3">
                      {clinicBranding.logoUrl ? (
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white p-1.5">
                          <img
                            src={clinicBranding.logoUrl}
                            alt="Logomarca da clínica"
                            className="max-h-full max-w-full object-contain"
                            onError={() =>
                              setClinicBranding((current) => ({ ...current, logoUrl: '' }))
                            }
                          />
                        </div>
                      ) : (
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white/15">
                          <Building2 className="h-7 w-7" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="truncate font-bold">
                          {clinicBranding.name || user?.clinic_name || user?.full_name || 'Clínica Odontológica'}
                        </p>
                        <p className="text-sm text-purple-100">Nosso mês em números</p>
                      </div>
                    </div>

                    <div className="mt-8">
                      <p className="text-3xl font-extrabold capitalize sm:text-4xl">{monthlySummary.label}</p>
                      <p className="mt-2 text-sm text-indigo-100">
                        Cuidado, presença e crescimento em cada atendimento.
                      </p>
                    </div>

                    <div className="mt-7 grid grid-cols-2 gap-3">
                      {[
                        ['Pacientes atendidos', number(monthlySummary.patients)],
                        ['Consultas realizadas', number(monthlySummary.appointments)],
                        ['Novos pacientes', number(monthlySummary.newPatients)],
                        [
                          'Taxa de comparecimento',
                          monthlySummary.attendanceRate === null ? '—' : `${monthlySummary.attendanceRate}%`,
                        ],
                      ].map(([label, value]) => (
                        <div key={label} className="rounded-xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                          <p className="text-2xl font-extrabold sm:text-3xl">{value}</p>
                          <p className="mt-1 text-xs font-medium text-indigo-100 sm:text-sm">{label}</p>
                        </div>
                      ))}
                    </div>

                    {monthlySummary.patientChange !== null && (
                      <p className="mt-5 text-sm text-indigo-100">
                        {monthlySummary.patientChange >= 0 ? '▲' : '▼'}{' '}
                        {Math.abs(monthlySummary.patientChange)}% de pacientes atendidos em relação ao mês anterior.
                      </p>
                    )}

                    <div className="mt-8 flex items-end justify-between border-t border-white/15 pt-4 text-xs text-indigo-100">
                      <span>Resumo gerado pelo OdontoAssist AI</span>
                      <span className="font-bold text-white">OdontoAssist AI</span>
                    </div>
                  </div>
                </div>

                <aside className="space-y-4">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <h3 className="font-semibold text-gray-900">O que entra no resumo?</h3>
                    <p className="mt-2 text-sm leading-6 text-gray-600">
                      Pacientes únicos e consultas com status Realizado, novos cadastros e comparecimento entre atendimentos realizados e faltas.
                    </p>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                    A imagem não inclui nomes de pacientes, informações clínicas nem valores financeiros.
                  </div>
                  <button
                    type="button"
                    onClick={() => exportMonthlySummary()}
                    disabled={exportingSummary}
                    className="inline-flex w-full items-center justify-center rounded-lg bg-purple-600 px-4 py-3 text-sm font-semibold text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {exportingSummary ? 'Gerando imagem...' : 'Baixar imagem'}
                  </button>
                  <button
                    type="button"
                    onClick={() => exportMonthlySummary({ share: true })}
                    disabled={exportingSummary}
                    className="inline-flex w-full items-center justify-center rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 text-sm font-semibold text-purple-700 hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Share2 className="mr-2 h-4 w-4" />
                    Compartilhar
                  </button>
                  <p className="text-center text-xs text-gray-500">
                    No computador, a imagem será baixada quando o compartilhamento direto não estiver disponível.
                  </p>
                </aside>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
